"use client";

import { SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";

import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { resolveTprCasePolicyPackageId } from "@/configuration/groups/tpr/case-policies";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprNetworkStatus } from "@/configuration/groups/tpr/components/network-status";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TprReceivingAssignmentEditor } from "@/configuration/groups/tpr/components/receiving-assignment-editor";
import { TprSyncAuthButtons } from "@/configuration/groups/tpr/components/sync-auth-buttons";
import {
  refreshTprWorkflowProgress,
  useTprLiveWorkflowRefresh,
} from "@/configuration/groups/tpr/components/workflow-progress";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import { resolveTprNonCamelbackBucket, TprSyncBucket } from "@/configuration/groups/tpr/sync-bucket";
import type { TprDocumentUpload } from "@/configuration/groups/tpr/workflows/classifications";
import {
  getTprManualBypassStep,
  isTprContinuedFailure,
  isTprEligibilityTimeoutReview,
  isTprStoppedOnStep,
} from "@/configuration/groups/tpr/workflows/manual-step-continuation";
import {
  describeTprSpecialtyCategories,
  matchTprDiagnosisSpecialties,
} from "@/configuration/groups/tpr/workflows/physician-roster";
import {
  tprReferralAuthNeedsPendingUpload,
  tprReferralAuthNeedsReview,
} from "@/configuration/groups/tpr/workflows/referral-auth-status";
import type {
  StediEligibilityDisplayLabel,
  TprSyncChecklistTraceEntry,
  TprSyncChecklistUrgency,
} from "@/configuration/groups/tpr/workflows/sync-checklist";
import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaCoverageResolutionDialog } from "@/src/components/tasks/athena-coverage-resolution-dialog";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { TaskStepCard } from "@/src/components/tasks/task-step-card";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import {
  useTaskStepVerifications,
  useApproveTaskMutation,
  useLocalTaskUpdateMutation,
} from "@/src/lib/data-fetchers/tasks-fetchers";
import {
  findPatientContactSyncGaps,
  isCompleteAddress,
  PatientContactSyncGap,
} from "@/src/lib/ehr/patient-contact-sync";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import { errorFromStepOutput } from "@/src/lib/workflows/step-failure-output";
import {
  ProviderSelectionStep,
  type ReceivingAssignmentOutput,
} from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type {
  AthenaCoverageReadbackOutput,
  TprInsuranceRequirementsOutput,
  TprReferralAuthorizationOutput,
} from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, CoverageType, getCoverageDisplayName, getSubscriberName } from "@/src/models/coverage";
import type { PriorAuthorization } from "@/src/models/documents/prior-authorization/prior-authorization";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import {
  getPatientDisplayName,
  getPatientFaxNumber,
  getPatientName,
  getPatientPhoneNumber,
  type Patient,
} from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import type { ProviderInsuranceRequirements } from "@/src/models/provider-insurance-requirements";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
import {
  resolveTaskStepPhase,
  TaskStepPhase,
  type PinnableTaskStepPhase,
  type TaskStepCardState,
} from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";

const NOT_AVAILABLE = "N/A";

type TprCoverageSlot = { label: string; title: string };

function isTprCasePolicyCoverage(coverage: Coverage | null | undefined): boolean {
  return coverage?.coverageType === CoverageType.INSURANCE && resolveTprCasePolicyPackageId(coverage) !== null;
}

function getTprCoverageSlots(
  primary: Coverage | null | undefined,
  secondary: Coverage | null | undefined
): { primary: TprCoverageSlot; secondary: TprCoverageSlot } {
  const primaryCasePolicy = isTprCasePolicyCoverage(primary);
  const secondaryCasePolicy = isTprCasePolicyCoverage(secondary);
  const casePolicySlot = (position: number): TprCoverageSlot => {
    const label = primaryCasePolicy && secondaryCasePolicy ? `Case Policy ${position}` : "Case Policy";

    return { label, title: label };
  };

  return {
    primary: primaryCasePolicy ? casePolicySlot(1) : { label: "Primary", title: "Primary Insurance" },
    secondary: secondaryCasePolicy ? casePolicySlot(2) : { label: "Secondary", title: "Secondary Insurance" },
  };
}

/**
 * Lowercased opening segment every TPR disposition note carries — see `getUnifiedReferralInternalNote`
 * in configuration/groups/tpr/workflows/referral-notes.ts. Matched anywhere in the note (not just at
 * the start) because Athena appends internal notes server-side, so ours can end up after an earlier
 * one. Used to tell "Athena already has our note" apart from "Athena has some other note" — e.g.
 * `careTeamStep`'s referring-provider address-mismatch note, which is written earlier and carries no
 * such segment.
 */
const REVIEWED_BY_VALERIE_HEALTH_MARKER = "reviewed by valerie health";

const TPR_MANUAL_REVIEW_BUCKET = TprSyncBucket.BILTMORE_CARD_REFERRAL;

/**
 * Plain-English labels for the real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  [TprSyncBucket.CLOSED]: "Closed — upcoming appointment already scheduled",
  [TprSyncBucket.SS_CARDIO]: "SS Cardio — stays with practice",
  [TprSyncBucket.REFERRALS_CARDIOLOGY]: "referralscardiology — AHI / Dr. Jamal / Dr. Mix referral",
  [TprSyncBucket.BILTMORE_CARD_IO_SCHED]: "Biltmore — ready to schedule",
  [TprSyncBucket.BILTMORE_COUMADIN]: "biltmorecoumadin — Coumadin referral",
  [TprSyncBucket.PULMONARY_REFERRALS]: "pulmonaryreferrals — Pulmonology referral",
  [TprSyncBucket.CARDIOTHORACIC_STAFF]: "cardiothoracicstaff — Cardiothoracic surgery referral",
};

function getSuggestedBucketLabel(bucketLabel: string, closeReason: string | null): string {
  if (bucketLabel === "closed" && closeReason) return `Closed — ${closeReason}`;

  return SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel;
}

const NO_CONFIDENT_BUCKET_SUGGESTION =
  "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields({ provider }: { provider: Practitioner | null }) {
  const name = (provider && getPractitionerNameWithCredentials(provider)) || NOT_AVAILABLE;
  const npi = provider?.npiNumber || NOT_AVAILABLE;

  return (
    <>
      Name: <CopyableContent text={name} />, NPI: <CopyableContent text={npi} />
    </>
  );
}

function ResolvedProviderCopyFields({
  providerLabel,
  providerNpi,
  writeSafe,
}: {
  providerLabel: string | null;
  providerNpi: string | null;
  writeSafe: boolean;
}) {
  return (
    <>
      Name: <CopyableContent text={providerLabel || NOT_AVAILABLE} />, NPI:{" "}
      <CopyableContent text={providerNpi || NOT_AVAILABLE} />
      {!writeSafe && " — verify manually before entry"}
    </>
  );
}

function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;

  const display = getCoverageDisplayName({ coverage, includesPlan: true, includeSubscriberName: true });

  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/**
 * Who the plan is actually held by. Ops keys the subscriber into Athena separately from the
 * patient, so a plan held by a spouse or parent has to be visible on the card rather than buried
 * in "More insurance detail".
 *
 * `relationshipToSubscriber` is the *patient's* relationship to the insured, so it is phrased as
 * "patient is Child" — "John Smith (Child)" reads as though John is the child. Self is never
 * shown: `coverageSchema` fills an uncaptured relationship in as Self, so it can't be told apart
 * from a real one, and a bare "Self" would tell ops the patient holds a plan nobody captured.
 */
function getPolicyHolder(coverage: Coverage | null | undefined): string | null {
  if (!coverage || coverage.coverageType === CoverageType.SELF_PAY) return null;

  const name = getSubscriberName(coverage);
  const relationship =
    coverage.relationshipToSubscriber && coverage.relationshipToSubscriber !== RelationshipToSubscriber.SELF
      ? `patient is ${coverage.relationshipToSubscriber}`
      : null;

  if (!name) return relationship ? `Name not captured (${relationship})` : null;

  return relationship ? `${name} (${relationship})` : name;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;

  return `${date} - ${type} (${status})`;
}

function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;

    return bTime - aTime;
  });
}

type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(
  label: string,
  referralValue: string | null,
  chartValue: string | null
): DataSourceEntry {
  if (referralValue) return { label, value: referralValue, source: "Referral", missing: false };
  if (chartValue) return { label, value: chartValue, source: "Patient Chart", missing: false };

  return { label, value: NOT_AVAILABLE, source: "N/A", missing: true };
}

type TimelineStatus = TaskStepCardState;

/**
 * Stable identity for each TPR checklist step, used as `task_step_verification.step_key`
 * so recorded accuracy stays comparable when steps are renumbered, retitled, or reordered. Never
 * derive this from `number` or `title` — both change; these slugs must not.
 */
export const TPR_TASK_STEP_KEY = {
  LOCATE_DOCUMENT: "locate_document",
  PROVIDER_LOCATION: "provider_location",
  DEMOGRAPHICS_CARE_TEAM: "demographics_care_team",
  INSURANCE: "insurance",
  REFERRAL_AUTHORIZATION: "referral_authorization",
  OUTBOUND_FAXES: "outbound_faxes",
  FULL_REGISTRATION: "full_registration",
  PROCESS_REFERRAL_DOCUMENT: "process_referral_document",
  DOCUMENT_UPLOAD: "document_upload",
  QUICKVIEW: "quickview",
  BUCKET_ASSIGNMENT: "bucket_assignment",
} as const;

export type TprTaskStepKey = (typeof TPR_TASK_STEP_KEY)[keyof typeof TPR_TASK_STEP_KEY];

type TimelineStep = {
  number: number;
  title: string;
  stepKey: TprTaskStepKey;
  status: TimelineStatus;
  /** See `TPR_PINNED_STEP_PHASE`. Omitted means the phase follows the status. */
  phase?: PinnableTaskStepPhase;
  /** Why this step needs a human, in place of the card's generic instruction. */
  detail?: string;
  summary: string;
  workflowStepKeys: StepKey[];
  /**
   * The few fields worth seeing on a card that is still waiting its turn — what automation
   * decided, without the evidence behind it. Items with no value are dropped, so a step the
   * workflow hasn't reached shows nothing rather than a row of N/A.
   */
  preview?: DecisionMetadataItem[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control?: React.ReactNode;
  };
};

/**
 * Steps whose phase is a property of the step itself, not of how cleanly automation happened to
 * resolve it on this referral.
 *
 * These are the TPR steps automation was never built to finish: outbound faxes are still
 * partially manual, the referral document needs a human to key parts of it into Athena, and the
 * bucket is ops' judgement call. They stay `ACTION_NEEDED` even when every workflow step under
 * them completed, because "completed" there only means the automated part is done — the human
 * part still isn't.
 *
 * Referral Authorization is pinned per-referral rather than here: it's `ACTION_NEEDED` only when
 * it needs an outbound fax, and plain `QA` when adding the authorization object is the whole job.
 *
 * Every other step is automated end to end, so it falls through to its status: `QA` when it
 * worked, `FAILED` when it didn't. A failed sync outranks a pin — see `resolveTaskStepPhase`.
 */
const TPR_PINNED_STEP_PHASE: Partial<Record<TprTaskStepKey, PinnableTaskStepPhase>> = {
  [TPR_TASK_STEP_KEY.OUTBOUND_FAXES]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT]: TaskStepPhase.ACTION_NEEDED,
};

export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};

export type TprChartReferralAuth = {
  referralAuthId: string;
  number: string | null;
  startDate: string | null;
  endDate: string | null;
  expired: boolean | null;
};

export type TprChartRequestLetter = {
  documentEhrId: string;
  documentDate: string | null;
  description: string | null;
};

export type TprChartDocument = {
  documentEhrId: string;
  name: string;
  documentDate: string | null;
};

type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientHomePhoneNumber: string;
  patientMobilePhoneNumber: string;
  patientWorkPhoneNumber: string;
  patientEmail: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  dateReceived: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  primaryPolicyHolder: string;
  /** Null when the patient genuinely has no secondary plan, as opposed to an uncaptured one. */
  secondaryPlanLabel: string | null;
  secondarySubscriberId: string | null;
  secondaryPolicyHolder: string | null;
  isSecondarySpecialCaseInsurance: boolean;
  /**
   * The authorization terms as extracted from the referral. Ops needs the whole set — a number with
   * no validity window or visit count can't be checked against what Athena holds.
   */
  authorizationNumber: string;
  authorizationStatus: string;
  authorizationStartDate: string;
  authorizationEndDate: string;
  authorizationVisits: string;
  /** Separate from `authorizationVisits`: "services" and "visits" are distinct extracted fields. */
  authorizationServices: string | null;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  primaryCoverageSlot: TprCoverageSlot;
  secondaryCoverageSlot: TprCoverageSlot;
  diagnosis: string;
  /** Which of TPR's four sub-specialty categories the diagnosis text maps to (General /
   * Interventional / Structural / EP) — the bucket the provider resolver matched providers against. */
  diagnosisCategories: string;
};

/**
 * The condensed body of a card that hasn't come up yet.
 *
 * Drops items with no value, so a step the workflow genuinely hasn't reached renders nothing
 * instead of a row of N/A — the point is to show what was decided, and nothing has been yet.
 */
function TimelineStepPreview({ items }: { items?: DecisionMetadataItem[] }) {
  const resolved = (items ?? []).filter((item) => item.value && item.value !== NOT_AVAILABLE);

  return resolved.length > 0 ? <DecisionMetadata items={resolved} /> : null;
}

export function DecisionMetadata({ items }: { items: DecisionMetadataItem[] }) {
  return (
    <dl className="flex flex-wrap gap-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"
        >
          <dt className="text-muted-foreground">{item.label}</dt>
          <dd className="font-medium text-foreground">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;

function providerSelectionReason(
  selectedStep: ProviderSelectionEvidence["selectedStep"],
  internalReferral = false
): string {
  switch (selectedStep) {
    case ProviderSelectionStep.UPCOMING_APPOINTMENT:
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case ProviderSelectionStep.PAST_APPOINTMENT:
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case ProviderSelectionStep.USUAL_PROVIDER:
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case ProviderSelectionStep.REFERRAL_PROVIDER:
      return internalReferral
        ? "Selected because this is an internal Biltmore-to-Biltmore referral, so the provider named on the referral takes precedence over appointment history."
        : "Selected because this provider is named on the referral.";
    case ProviderSelectionStep.DIAGNOSIS_MATCH:
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}

function ProviderSelectionChecklist({
  evidence,
  upcomingAppointmentProviderSummary,
  referralProviderName,
  referralProviderNpi,
  diagnosis,
  diagnosisCategories,
  selectedProviderName,
  selectedLocation,
}: {
  evidence: ProviderSelectionEvidence;
  upcomingAppointmentProviderSummary: string | null;
  referralProviderName: string;
  referralProviderNpi: string;
  diagnosis: string;
  diagnosisCategories: string;
  selectedProviderName: string;
  selectedLocation: string;
}) {
  const hasQualificationEvidence = evidence.appointmentProviders.some(
    (provider) =>
      provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined
  );
  const providerCountSummary = (period: "past" | "upcoming"): string => {
    const providers = evidence.appointmentProviders.flatMap((provider) => {
      const count =
        period === "past"
          ? (provider.qualifyingPastAppointmentCount ?? 0)
          : (provider.qualifyingUpcomingAppointmentCount ?? 0);
      const locationEhrId =
        period === "past" ? provider.mostRecentPastLocationEhrId : provider.earliestUpcomingLocationEhrId;
      const location = locationEhrId
        ? (getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}`)
        : null;

      return count > 0
        ? [
            `${provider.providerName}${provider.providerNpi ? ` · ${provider.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`,
          ]
        : [];
    });

    if (providers.length > 0) return providers.join(", ");

    if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
      return upcomingAppointmentProviderSummary;
    }

    if (
      !hasQualificationEvidence &&
      (evidence.selectedStep === ProviderSelectionStep.USUAL_PROVIDER || evidence.appointmentProviders.length > 0)
    ) {
      return "Not captured for this legacy task";
    }

    return "None";
  };

  const upcomingAppointmentStep = {
    key: ProviderSelectionStep.UPCOMING_APPOINTMENT as const,
    title: "Check for an upcoming appointment",
    items: [
      { label: "Upcoming within 60 days", value: evidence.upcomingAppointmentCount },
      { label: "Qualifying appointments at Biltmore locations", value: providerCountSummary("upcoming") },
    ],
  };
  const pastAppointmentStep = {
    key: ProviderSelectionStep.PAST_APPOINTMENT as const,
    title: "Check past appointments at Biltmore locations",
    items: [
      { label: "Completed in past 3 years", value: evidence.pastAppointmentCount },
      { label: "Qualifying appointments at Biltmore locations", value: providerCountSummary("past") },
    ],
  };
  const referralProviderStep = {
    key: ProviderSelectionStep.REFERRAL_PROVIDER as const,
    title: "Use the provider named on the referral",
    items: [
      { label: "Provider", value: referralProviderName },
      { label: "NPI", value: referralProviderNpi },
    ],
  };
  const diagnosisMatchStep = {
    key: ProviderSelectionStep.DIAGNOSIS_MATCH as const,
    title: "Sanity-check the pick or find a replacement",
    items: [
      { label: "Diagnosis", value: diagnosis },
      { label: "Diagnosis category", value: diagnosisCategories },
      { label: "Location", value: selectedLocation },
      { label: "Final provider", value: selectedProviderName },
    ],
  };
  // Listed in the order the resolver actually evaluated them. An internal Biltmore-to-Biltmore
  // referral checks the provider named on the referral first, so showing it third would tell ops
  // the pick came from exhausting appointment history when it did not.
  const steps: Array<{
    key: ProviderSelectionStep;
    title: string;
    items: DecisionMetadataItem[];
  }> = evidence.internalReferral
    ? [referralProviderStep, upcomingAppointmentStep, pastAppointmentStep, diagnosisMatchStep]
    : [upcomingAppointmentStep, pastAppointmentStep, referralProviderStep, diagnosisMatchStep];

  return (
    <details className="space-y-2 border-t pt-3">
      <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>
      <div className="mt-2 space-y-2">
        <ol className="space-y-2">
          {steps.map((step, index) => {
            const selected = evidence.selectedStep === step.key;

            return (
              <li
                key={step.key}
                className={
                  selected
                    ? "space-y-2 rounded-md border border-foreground/40 bg-muted p-3"
                    : "space-y-2 rounded-md border bg-muted/10 p-3"
                }
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">
                    Step {index + 1} — {step.title}
                  </span>
                  {selected ? (
                    <Badge variant="outline" className="border-foreground/40 bg-muted font-medium text-foreground">
                      Selected
                    </Badge>
                  ) : null}
                </div>
                <DecisionMetadata
                  items={
                    selected
                      ? [
                          ...step.items,
                          { label: "Picked provider", value: selectedProviderName },
                          {
                            label: "Reason",
                            value: providerSelectionReason(evidence.selectedStep, evidence.internalReferral),
                          },
                        ]
                      : step.items
                  }
                />
              </li>
            );
          })}
        </ol>
        {evidence.selectedStep === ProviderSelectionStep.USUAL_PROVIDER ? (
          <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">
            This assignment was created with the retired Athena usual-provider rule. Verify the provider and location
            against qualifying appointment history before following it.
          </div>
        ) : null}
      </div>
    </details>
  );
}

function getTprInsuranceStepSummary(input: {
  coverageApproval: TaskApproval | null;
  isSelfPay: boolean;
  hasInsuranceRequirements: boolean;
  inNetworkDisplay: string;
  insuranceStatusDisplay: string;
  coverageReadbackComplete: boolean;
  confirmedNonCamelbackAssignment: boolean;
  assignmentRequiresManualReview: boolean;
}): string {
  if (input.coverageApproval) {
    return input.coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing.";
  }

  if (input.isSelfPay) return "Self Pay — no network or eligibility checks apply.";

  if (input.hasInsuranceRequirements) {
    return `In network: ${input.inNetworkDisplay}. Athena eligibility: ${input.insuranceStatusDisplay}.`;
  }

  if (input.coverageReadbackComplete && input.confirmedNonCamelbackAssignment) {
    return "Coverage confirmed. Athena eligibility and network checks: Not applicable, non-Camelback location.";
  }

  if (input.coverageReadbackComplete) {
    return "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions.";
  }

  if (input.assignmentRequiresManualReview) {
    return "Not run because the receiving provider and location require manual confirmation.";
  }

  return "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.";
}

function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map((key) => taskSteps.find((step) => step.stepKey === key)).filter(Boolean);

  if (rows.some((row) => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some((row) => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }

  if (keys.every((key) => taskSteps.some((step) => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "completed";
  }

  return "waiting";
}

function TimelineStepCard({
  task,
  step,
  failedStepKey,
  bypassStepKey,
  failureReason,
  continuedPastFailure,
  signOffOnContinue,
  blockedBy,
  awaitingStep,
}: {
  task: Task;
  step: TimelineStep;
  failedStepKey: StepKey | null;
  bypassStepKey: StepKey | null;
  /** What the failed step reported, when it recorded a reason. */
  failureReason: string | null;
  /** The failure is one the run carried on past, rather than the one the task is stopped on. */
  continuedPastFailure: boolean;
  /** Continue also confirms the card, because every step on it has already run. */
  signOffOnContinue: boolean;
  /** The failed step holding the workflow up, when this card is waiting behind it. */
  blockedBy: TimelineStep | null;
  /** The earlier step ops has yet to settle, when this card is only waiting its turn in the queue. */
  awaitingStep: TimelineStep | null;
}) {
  /**
   * A card waiting its turn presents as `not_run`, which carries no phase and so no colour, label
   * or verdict control — the same neutral state as a step the workflow hasn't reached. Rewriting
   * the status rather than suppressing the phase keeps one source of truth: `TaskStepCard` resolves
   * its own phase, so handing it a phase computed out here would let the two disagree.
   */
  const effectiveStatus = failedStepKey ? "failed" : awaitingStep ? "not_run" : step.status;
  const approvalMutation = useApproveTaskMutation();
  const localTaskUpdateMutation = useLocalTaskUpdateMutation({ invalidateOnSettled: false });
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");
  // Names the workflow step that actually failed. A card can stand for up to seven of them —
  // Insurance covers the coverage write, the readback, eligibility and the network rules — so
  // "Failed" on its own contradicts the body when the part that failed isn't the part the body
  // reports on, e.g. a red card sitting above "Synced to Athena: Yes".
  const continuation = failedStepKey
    ? {
        instruction:
          failedStepKey === StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY
            ? "The eligibility check failed. Verify or correct the coverage in Athena, then recheck eligibility to save the result before continuing."
            : continuedPastFailure
              ? `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed${failureReason ? ` (${failureReason})` : ""}. The rest of the workflow ran anyway. Complete this step in Athena, then continue.`
              : `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed${failureReason ? ` (${failureReason})` : ""}. Complete it manually in Athena, then continue with the remaining steps.`,
        control: (
          <TprManualStepContinueButton
            task={task}
            stepKey={failedStepKey}
            signOff={signOffOnContinue ? step : undefined}
          />
        ),
      }
    : step.continuation;
  const handleVerificationComplete = async () => {
    await approvalMutation.mutateAsync({ taskId: task.id, approved: true });
    localTaskUpdateMutation.mutate({ ...task, status: TaskStatus.PROCESSING });
    refreshTprWorkflowProgress(invalidateInstructions);
  };

  return (
    <TaskStepCard
      header={`${step.number}. ${step.title}`}
      status={effectiveStatus}
      phase={step.phase}
      /**
       * A continuation with no control isn't an action, it's a sentence — some steps explain what
       * ops should do and then rely on the card's own button to release the gate. Passing it as
       * `action` anyway rendered an instruction above an empty div *and* suppressed the card's
       * button, which is how failed Insurance ended up with no way forward at all. Its text
       * becomes the card's `detail` instead.
       */
      detail={
        blockedBy
          ? `Waiting on step ${blockedBy.number} (${blockedBy.title}), which failed.`
          : (!continuation?.control && continuation?.instruction) || step.detail
      }
      taskId={task.id}
      stepKey={step.stepKey}
      workflowStepKeys={step.workflowStepKeys}
      onVerificationComplete={step.continuation ? handleVerificationComplete : undefined}
      action={
        continuation?.control || bypassStepKey ? (
          <div className="space-y-3">
            {continuation?.instruction ? (
              <p className="text-sm text-muted-foreground">{continuation.instruction}</p>
            ) : null}
            {continuation?.control ? <div className="flex justify-center">{continuation.control}</div> : null}
            {bypassStepKey ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Continue past this check without marking it verified. Your override will be recorded.
                </p>
                <TprManualStepContinueButton task={task} stepKey={bypassStepKey} bypass />
              </div>
            ) : null}
          </div>
        ) : undefined
      }
    >
      {/* A card waiting its turn shows what automation decided but not the evidence behind it —
          enough to read the referral at a glance, without the checklists and flags belonging to a
          step ops can't act on yet. */}
      {awaitingStep ? <TimelineStepPreview items={step.preview} /> : step.content}
    </TaskStepCard>
  );
}

function TprStediEligibilityValues({ referralId, coverage }: { referralId: string; coverage: Coverage | null }) {
  const { eligibilityCheck } = useStediEligibilityCheck(referralId, coverage);

  return (
    <div className="space-y-3">
      <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />
      <p className="text-sm text-muted-foreground">Coverage rechecks and edits are available on the Information tab.</p>
    </div>
  );
}

function TprChartReferralAuthDocuments({ documents }: { documents: TprChartDocument[] | null }) {
  return (
    <details className="rounded-md border p-3 text-sm">
      <summary className="cursor-pointer font-medium">
        Possible associated documents{documents ? ` (${documents.length})` : ""}
      </summary>
      <div className="mt-2">
        {documents === null ? (
          <p className="text-muted-foreground">Could not load the Athena chart documents. Check the chart directly.</p>
        ) : documents.length === 0 ? (
          <p className="text-muted-foreground">No referral or authorization documents on the Athena chart.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-5">
            {documents.map((document) => (
              <li key={document.documentEhrId}>
                {document.name} · <CopyableContent text={document.documentEhrId} textClassName="text-xs" />
                {document.documentDate ? ` · ${document.documentDate}` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function TprSyncTimeline({
  task,
  referralId,
  coverage,
  taskSteps,
  pendingApproval,
  receivingAssignmentConfirmed,
  followThroughEligible,
  coverageReadback,
  athenaCoverageLive,
  insuranceRequirements,
  referralAuthorization,
  resolvedReceiving,
  patientLabel,
  patientEhrId,
  documentId,
  bucketLabel,
  closeReason,
  internalNoteToEnter,
  urgency,
  actionNote,
  decisionTrace,
  insuranceStatusDisplay,
  hasMissingInformation,
  unresolvedUpcomingProviders,
  upcomingAppointmentProviderSummary,
  upcomingAppointments,
  pastAppointments,
  documentUploads,
  privacyNoticeOnFile,
  referralSourceOnFile,
  isPrimaryCareProvider,
  referringProviderMarkedPcp,
  primaryCareProviderAlreadyPresent,
  patientDisplayId,
  opsCrossCheck,
  patientContactSyncGaps,
  chartReferralAuths,
  chartRequestLetters,
  isReferralAuth,
  priorAuthorizationDocumentId,
  chartReferralAuthDocuments,
}: {
  task: Task;
  referralId: string;
  coverage: Coverage | null;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  /** Live Athena coverages for this render, so an Athena-side insurance edit shows up immediately. */
  athenaCoverageLive: { primary: Coverage | null; secondary: Coverage | null };
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  resolvedReceiving: TprReferralInstructionsProps["resolvedReceiving"];
  patientLabel: string;
  patientEhrId: string;
  documentId: string;
  bucketLabel: string;
  closeReason: string | null;
  /** The note a reviewer should enter in Athena — Athena's own note once it carries ours, else the
   * previewed "Reviewed By Valerie Health (...) - <provider> - ..." note. Resolved once in
   * `TprReferralInstructions` so both views show the same text. */
  internalNoteToEnter: string;
  /** Athena document priority (Urgent vs Normal) and why — Step 9's urgency line. */
  urgency: TprSyncChecklistUrgency;
  /** The decision-tree's short action-note label ("Reviewed By Valerie Health (...) / <label>"). */
  actionNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  insuranceStatusDisplay: string;
  hasMissingInformation: boolean;
  unresolvedUpcomingProviders: TprReferralInstructionsProps["unresolvedUpcomingProviders"];
  upcomingAppointmentProviderSummary: string | null;
  upcomingAppointments: Appointment[];
  pastAppointments: Appointment[];
  documentUploads: TprDocumentUpload[];
  privacyNoticeOnFile: boolean | null;
  /** Athena's current `referralsourceid`, or null when blank — see `tryGetPatientReferralSourceId`. */
  referralSourceOnFile: string | null | undefined;
  isPrimaryCareProvider: boolean;
  referringProviderMarkedPcp: boolean;
  primaryCareProviderAlreadyPresent: boolean;
  patientDisplayId: string | null;
  opsCrossCheck: TprOpsCrossCheck;
  /** Contact details we hold that the live Athena chart doesn't — see `findPatientContactSyncGaps`. */
  patientContactSyncGaps: PatientContactSyncGap[];
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
  isReferralAuth: boolean;
  priorAuthorizationDocumentId: string | null;
  chartReferralAuthDocuments: TprChartDocument[] | null;
}) {
  // Keeps the step cards in step with the EHR: each tick re-runs the server component, re-issuing
  // its live Athena reads, so an insurance or provider change made in Athena lands here on its own.
  useTprLiveWorkflowRefresh(useInvalidateServerComponent("getReferralInstructions"));

  /**
   * Reads the same persisted verdict the card header writes, so the provider/location editor can
   * stay hidden until ops has actually marked the assignment wrong. Queried here rather than
   * threaded down from `TaskStepCard`: it is server state any component can ask for, not a card
   * internal.
   */
  const { verdictByStepKey } = useTaskStepVerifications(task.id);
  const providerAssignmentMarkedIncorrect =
    verdictByStepKey.get(TPR_TASK_STEP_KEY.PROVIDER_LOCATION)?.verdict === VerificationResult.INCORRECT;

  const isProviderFirst = taskSteps.some((step) => step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT);

  const providerSelectionEvidence = resolvedReceiving.providerSelectionEvidence ?? {
    selectedStep: null,
    upcomingAppointmentCount: upcomingAppointments.length,
    pastAppointmentCount: pastAppointments.length,
    internalReferral: false,
    appointmentProviders: [],
  };
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  const assignmentStatus = isProviderFirst
    ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT])
    : workflowStillProcessing
      ? "waiting"
      : "not_run";
  const assignmentDependentStatus: TimelineStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
  const receivingAssignmentApproval =
    pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
  const assignmentRequiresManualReview =
    assignmentStatus === "completed" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
  const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === ProviderSelectionStep.USUAL_PROVIDER;
  const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
  const assignmentWasManuallyConfirmed =
    assignmentStatus === "completed" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
  /**
   * An assignment the resolver flagged for manual review is a spot-check, not a failure.
   *
   * `FAILED` means automation owned the step and it didn't land — for this card, that no receiving
   * provider was chosen. Held-for-review is the opposite: the resolver *did* pick a provider and a
   * location and is asking a human to rule on the pair, which is `VERIFICATION_REQUIRED`. Red also
   * suppressed the explanation, since the reasons list and the summary line below only render for a
   * review, so ops got a Failed card on a complete assignment with nothing saying why.
   *
   * QA rather than `ACTION_NEEDED` because the difference is what ops is offered: QA carries the
   * Correct / Incorrect pair, and Incorrect is what opens the provider/location picker. An
   * action-needed card offers a single Updated button and would leave ops no way to say the pick
   * was wrong.
   *
   * The retired usual-provider rule is the same shape: it produced a provider nobody validated
   * against appointment history, so it wants checking rather than reporting as a defect.
   */
  const assignmentReviewStatus: TimelineStatus = assignmentWasManuallyConfirmed
    ? "completed"
    : assignmentRequiresManualReview || usesRetiredUsualProviderRule
      ? "verification_required"
      : assignmentDependentStatus;
  /** Whether the pick, complete as it is, still wants a human to ratify it before downstream work. */
  const assignmentNeedsReview =
    !assignmentWasManuallyConfirmed && (assignmentRequiresManualReview || usesRetiredUsualProviderRule);
  const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
  /**
   * A receiving assignment with no provider or no location is a failed selection, not something for
   * ops to "verify" — every downstream Athena write (care team, coverage department, document
   * routing) needs both, so the card goes red and says Failed instead of showing N/A on a card
   * that only asks ops to check it. Only asserted once the step has actually run, so a
   * still-queued assignment keeps
   * its waiting/not_run state rather than flashing red.
   */
  const assignmentSelectionIncomplete =
    !resolvedReceiving.providerLabel?.trim() ||
    !resolvedReceiving.providerNpi?.trim() ||
    !resolvedReceiving.locationLabel?.trim();
  const assignmentResolved = assignmentStatus === "completed" || assignmentStatus === "failed";
  const assignmentConfirmationStatus: TimelineStatus =
    assignmentResolved && assignmentSelectionIncomplete
      ? "failed"
      : assignmentWasManuallyConfirmed
        ? "completed"
        : assignmentReviewStatus === "verification_required"
          ? "verification_required"
          : assignmentStatus === "completed"
            ? gateStatus
            : assignmentStatus;
  /**
   * When ops can open the provider/location picker: the step failed, or ops rejected the pick.
   *
   * Nothing else. A pick held for manual review is a spot-check, not a failure, so it presents as
   * QA with the Correct / Incorrect pair — and Incorrect is what opens the editor. Offering the
   * picker on the review itself put an edit control on every flagged assignment before anyone had
   * said the pick was wrong.
   *
   * `failed` keeps it because there is no pick there to reject: an incomplete selection offers no
   * Incorrect button, so without this ops could never reach the editor in the one case it was
   * built for.
   */
  const providerAssignmentEditable = providerAssignmentMarkedIncorrect || assignmentConfirmationStatus === "failed";
  const confirmedNonCamelbackAssignment =
    assignmentStatus === "completed" && !followThroughEligible && receivingAssignmentApproval === null;
  const skippedFollowThroughStatus: TimelineStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
  const skippedFollowThroughSummary = assignmentRequiresManualReview
    ? "Not run because the receiving provider and location require manual confirmation."
    : confirmedNonCamelbackAssignment
      ? "Not needed for the resolved non-Camelback receiving assignment."
      : "Not run because the receiving assignment has not completed.";
  const coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
  const eligibilityTimedOut = taskSteps.some(
    (step) => isTprEligibilityTimeoutReview(step) && isTprStoppedOnStep(task.status, taskSteps, step)
  );
  const opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
  const opsKind = opsApproval?.metadata?.kind ?? null;
  const manualBypassStepKey = getTprManualBypassStep(task.status, taskSteps, pendingApproval)?.stepKey as
    | StepKey
    | undefined;

  const waitingForAssociatedReferral = taskSteps.some(
    (step) => step.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step.status === TaskStepStatus.PAUSED
  );
  const coverageReadbackComplete = taskSteps.some(
    (step) => step.stepKey === StepKey.READ_ATHENA_COVERAGE && step.status === TaskStepStatus.COMPLETED
  );
  const authRequestUploaded = taskSteps.some(
    (step) =>
      step.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD &&
      step.status === TaskStepStatus.COMPLETED &&
      step.output?.uploaded === true
  );
  const notesComplete = taskSteps.some(
    (step) => step.stepKey === StepKey.REFERRAL_DISPOSITION && step.status === TaskStepStatus.COMPLETED
  );
  const missingInfoUploadComplete = taskSteps.some(
    (step) => step.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step.status === TaskStepStatus.COMPLETED
  );
  const followThroughStatus = (keys: StepKey[]): TimelineStatus =>
    followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
  const resolveConsolidatedStatus = (statuses: TimelineStatus[]): TimelineStatus => {
    if (statuses.includes("failed")) return "failed";
    if (statuses.includes("review_needed")) return "review_needed";
    if (statuses.includes("verification_required")) return "verification_required";
    if (statuses.includes("waiting")) return "waiting";
    if (statuses.includes("completed")) return "completed";
    if (statuses.includes("not_run")) return "not_run";

    return "not_needed";
  };

  /**
   * What Athena holds right now. Falls back to the persisted readback's plan name only when the
   * live read returned nothing for that slot, so the display prefers current truth but never goes
   * blank just because a live Athena call came back empty.
   */
  const livePrimaryAthenaPlan =
    (athenaCoverageLive.primary && getCoverageDisplayName({ coverage: athenaCoverageLive.primary })) ||
    coverageReadback?.primary.planName ||
    NOT_AVAILABLE;
  const liveSecondaryAthenaPlan =
    (athenaCoverageLive.secondary && getCoverageDisplayName({ coverage: athenaCoverageLive.secondary })) ||
    coverageReadback?.secondary?.planName ||
    NOT_AVAILABLE;
  /**
   * Athena's insurance changed after we synced — almost always ops correcting it by hand. Worth
   * calling out explicitly: the network and eligibility decisions below were made against the
   * snapshot, so they may no longer describe the coverage that is actually on the chart.
   */
  const athenaCoverageDrifted = Boolean(
    coverageReadback &&
    athenaCoverageLive.primary?.ehrId &&
    athenaCoverageLive.primary.ehrId !== coverageReadback.primary.insuranceId
  );

  /**
   * Whether Athena actually holds a primary coverage — the live read first, the persisted readback
   * as a fallback when the live call came back empty.
   *
   * Deliberately not `coverageReadbackComplete`, which only says the read step *ran*. A read that
   * succeeded and found nothing satisfies that flag, which is how a referral with no coverage on
   * the chart could report "Synced to Athena: Yes" and sit in QA green.
   */
  const primaryCoverageInAthena = Boolean(athenaCoverageLive.primary?.ehrId ?? coverageReadback?.primary.insuranceId);
  // Keep sync status aligned with the chart. A pending approval separately pins ACTION_NEEDED
  // below so the resolution control remains available even when Athena already has a policy.
  //
  // Deliberately not conditioned on that approval. A run parked on a gate is REVIEW_NEEDED, not
  // PROCESSING, so `workflowStillProcessing` is already false in the case the pin exists for —
  // suppressing `waiting` would change nothing there. What it would change is a run that really is
  // in flight: the card would go red "Failed", force itself present and offer the coverage
  // resolution dialog, which then refuses with "no longer awaiting insurance review". The override
  // editor sets PROCESSING optimistically while the server-rendered approval is still stale, so
  // that is the common path, not a corner. The gate's own card is surfaced by `ownsPendingGate`
  // below instead, which does not depend on this status.
  const insuranceStatus: TimelineStatus = primaryCoverageInAthena
    ? "completed"
    : workflowStillProcessing
      ? "waiting"
      : "failed";

  const authorizationStatus =
    opsKind === "authorization_review"
      ? "verification_required"
      : opsKind === "manual_authorization"
        ? "review_needed"
        : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements)
          ? "review_needed"
          : referralAuthorization?.outcome === "not_needed"
            ? "not_needed"
            : referralAuthorization?.outcome === "needs_pending_request"
              ? "completed"
              : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
  const referralAuthNeedsPendingUpload = tprReferralAuthNeedsPendingUpload(referralAuthorization);
  /**
   * Whether step 5 involves sending something out, which is what decides its phase. Adding an
   * authorization object is fully automated and only wants a QA pass; a PCP referral fax or an
   * authorization request has a human in the loop, so the card has to ask ops to act.
   */
  const referralAuthNeedsOutboundFax =
    Boolean(insuranceRequirements?.referralMustBeFromPcp) || referralAuthNeedsPendingUpload;
  /**
   * Step 5 shows one bubble per requirement the plan actually has. An authorization requirement
   * implies a referral one, so both bubbles show and the authorization detail lives in the
   * authorization bubble; with only a referral requirement that detail moves into the referral
   * bubble. Manual review counts as required so ops still sees what to check.
   */
  const requirementApplies = (status: ProviderInsuranceRequirements["authorizationRequirement"] | undefined) =>
    status === "required" || status === "manual_review";
  const showPcpReferralRequirement = Boolean(insuranceRequirements?.referralMustBeFromPcp);
  const showAuthorizationRequirement = requirementApplies(insuranceRequirements?.authorizationRequirement);
  const showReferralRequirement =
    showAuthorizationRequirement || requirementApplies(insuranceRequirements?.referralRequirement);
  const createdChartReferralAuthId =
    referralAuthorization?.outcome === "created" ? referralAuthorization.referralAuthId : null;
  const documentAuthorizationNumber =
    opsCrossCheck.authorizationNumber === NOT_AVAILABLE ? null : opsCrossCheck.authorizationNumber.toLowerCase();
  // Only the chart objects that belong to this document: the one we wrote, or one carrying its auth number.
  const documentChartReferralAuths = chartReferralAuths.filter(
    (authorization) =>
      authorization.referralAuthId === createdChartReferralAuthId ||
      (documentAuthorizationNumber !== null &&
        authorization.number?.trim().toLowerCase() === documentAuthorizationNumber)
  );
  /* The authorization terms off the referral itself, shown in full: ops cannot check an Athena
     authorization against the document without the validity window and visit count alongside the
     number. */
  const authorizationDetails = (
    <div className="space-y-3">
      <DecisionMetadata
        items={[
          {
            label: "Auth number",
            value: <CopyableContent text={opsCrossCheck.authorizationNumber} textClassName="text-xs" />,
          },
          { label: "Status", value: opsCrossCheck.authorizationStatus },
          { label: "Start date", value: opsCrossCheck.authorizationStartDate },
          { label: "End date", value: opsCrossCheck.authorizationEndDate },
          { label: "Visits", value: opsCrossCheck.authorizationVisits },
          ...(opsCrossCheck.authorizationServices
            ? [{ label: "Services", value: opsCrossCheck.authorizationServices }]
            : []),
          {
            label: "Outcome",
            value: referralAuthorization
              ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome)
              : "Pending",
          },
          ...(referralAuthorization?.outcome === "created"
            ? [
                {
                  label: "Athena object",
                  value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />,
                },
                {
                  label: "Appointment",
                  value: referralAuthorization.needsManualAppointmentAttach
                    ? "Attach manually"
                    : referralAuthorization.appointmentAttached
                      ? "Attached"
                      : "None in window",
                },
              ]
            : []),
        ]}
      />
      {documentChartReferralAuths.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium">On the Athena chart</summary>
          <div className="mt-2 space-y-2">
            <ul className="list-disc space-y-1 pl-5">
              {documentChartReferralAuths.map((authorization) => (
                <li key={authorization.referralAuthId}>
                  <CopyableContent text={authorization.referralAuthId} textClassName="text-xs" />
                  {authorization.number ? ` · ${authorization.number}` : ""}
                  {authorization.startDate || authorization.endDate
                    ? ` · ${authorization.startDate ?? "—"}–${authorization.endDate ?? "—"}`
                    : ""}
                  {authorization.expired ? " · expired" : ""}
                </li>
              ))}
            </ul>
            {chartRequestLetters.length ? (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request letters</p>
                <ul className="list-disc space-y-1 pl-5">
                  {chartRequestLetters.map((letter) => (
                    <li key={letter.documentEhrId}>
                      <CopyableContent text={letter.documentEhrId} textClassName="text-xs" />
                      {letter.documentDate ? ` · ${letter.documentDate}` : ""}
                      {letter.description ? ` · ${letter.description}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </details>
      ) : null}
      <TprSyncAuthButtons
        canUploadLetter={referralAuthNeedsPendingUpload}
        enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)}
        taskId={task.id}
      />
    </div>
  );
  const pcpFaxStatus =
    opsKind === "pcp_referral_fax"
      ? "review_needed"
      : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp
        ? "not_needed"
        : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
  const authRequestUploadStatus =
    opsKind === "auth_request_upload"
      ? "review_needed"
      : referralAuthNeedsPendingUpload
        ? authRequestUploaded
          ? "completed"
          : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD])
        : "not_needed";
  const missingInfoFaxStatus: TimelineStatus = !hasMissingInformation
    ? "not_needed"
    : missingInfoUploadComplete
      ? "review_needed"
      : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
  const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
  const resultDocumentsToLabel = documentUploads.filter((upload) => upload.resultDocumentLabel !== null);
  const closeDocumentStatus =
    opsKind === "close_document" ? "review_needed" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
  const workflowNote = task.files.find((file) => file.type === FileType.INBOUND)?.note;
  const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
  /**
   * Step 1 is ops finding the chart, so the only thing its card turns on is whether the document
   * came in with an EHR ID attached — never on what `ENSURE_PATIENT` is doing. An id on the
   * document means automation found the chart and ops spot-checks the match; no id means ops has
   * to locate or create it themselves, which is work rather than a verdict.
   *
   * Deliberately never `waiting`: there is nothing to wait for. The id is either on the document
   * or it isn't, and either way ops can act now. `ensurePatientStatus` stays the workflow truth
   * that steps 3 and 7 gate on.
   */
  const patientChartStatus: TimelineStatus =
    !patientEhrId || patientEhrId === NOT_AVAILABLE ? "review_needed" : "verification_required";
  const fullRegistrationReviewReasons = [
    privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically")
      ? "Set Athena Privacy to YES manually."
      : null,
    // `createOrUpdatePatient` writes `referralsourceid` whenever Athena's is blank, so a blank
    // read-back means that write didn't land. Only asserted once the chart exists — before that
    // there is nothing to have written to.
    //
    // Strictly `null`, never `undefined`: the live refresh re-reads this every 60s, so treating a
    // failed read as blank would flip Full Registration to Failed on any transient Athena error
    // even where the value is set.
    ensurePatientStatus === "completed" && referralSourceOnFile === null
      ? 'Set "How did you hear about us" to PHYSICIAN REFERRAL manually.'
      : null,
  ].filter((reason): reason is string => reason !== null);
  const demographicsReviewReasons = [
    workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team")
      ? "Add the unmatched referring provider to the Athena care team manually."
      : null,
    // The patient write overrides all of these, so a value we hold that isn't on the chart means
    // that write didn't land — Athena drops a phone number it can't store without erroring, and a
    // partial address is never sent at all. Only asserted once the chart exists.
    ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.Phone)
      ? "Enter the patient's phone number in Athena manually — it did not sync."
      : null,
    ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.Email)
      ? "Enter the patient's email in Athena manually — it did not sync."
      : null,
    ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.HomeAddress)
      ? "Enter the patient's home address in Athena manually — it did not sync."
      : null,
  ].filter((reason): reason is string => reason !== null);
  /**
   * Mirrors `careTeamStep`'s own condition (`shouldAddTprReferringProviderAsPcp`): the intake
   * "Is PCP" checkbox always writes the primary-care recipient class; otherwise it is written when
   * the referring provider's specialty is primary care and the patient has no PCP on the chart yet.
   */
  const pcpDisposition = referringProviderMarkedPcp
    ? "Yes — marked Is PCP at intake"
    : !isPrimaryCareProvider
      ? "No — not a primary care specialty"
      : primaryCareProviderAlreadyPresent
        ? "No — PCP already on chart"
        : "Yes — added as the patient's PCP";
  const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
  /**
   * Full registration is automated, so a privacy notice automation couldn't set is a failure, not
   * work ops was always going to do by hand.
   *
   * The "How did you hear about us" default has no equivalent read-back — the card only tells ops
   * to set it if blank — so that half of the step can't be failed on until the workflow reports
   * whether it wrote the field.
   */
  const fullRegistrationStatus: TimelineStatus =
    ensurePatientStatus === "failed"
      ? "not_run"
      : ensurePatientStatus === "waiting"
        ? "waiting"
        : fullRegistrationReviewReasons.length > 0
          ? "failed"
          : privacyNoticeOnFile === null
            ? "verification_required"
            : "completed";
  /**
   * No API call writes the emergency contact — the workflow only surfaces what the referral
   * carried, and ops keys it into Athena by hand. So a referral that arrived with one turns step
   * 3 into work rather than a spot-check, and ops gets an Updated button instead of a verdict
   * pair. A failed care-team write still outranks this, per `resolveTaskStepPhase`.
   */
  const hasEmergencyContactToEnter =
    opsCrossCheck.emergencyContactName !== NOT_AVAILABLE || opsCrossCheck.emergencyContactPhoneNumber !== NOT_AVAILABLE;
  // A referring provider automation couldn't add to the care team is a failed API write, not an
  // action: the step is supposed to do this on its own.
  const demographicsStatus =
    ensurePatientStatus === "failed"
      ? "not_run"
      : careTeamStatus !== "completed"
        ? careTeamStatus
        : demographicsReviewReasons.length > 0
          ? "failed"
          : "completed";
  const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
  const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
  const inNetworkDisplay = !insuranceRequirements
    ? confirmedNonCamelbackAssignment
      ? "Not applicable, non-Camelback location"
      : unevaluatedInsuranceDecision
    : insuranceRequirements.clinicNetwork === "out_of_network" ||
        insuranceRequirements.providerNetwork === "out_of_network"
      ? "No"
      : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network"
        ? insuranceRequirements.networkVerification
          ? "Yes — confirmed by Ops"
          : "Yes — confirmed by insurance grid"
        : insuranceRequirements.clinicNetwork === "not_applicable" &&
            insuranceRequirements.providerNetwork === "not_applicable"
          ? "Not applicable"
          : "Manual review";
  const referralRequirementDisplay = insuranceRequirements
    ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement)
    : unevaluatedInsuranceDecision;
  const authorizationRequirementDisplay = insuranceRequirements
    ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement)
    : unevaluatedInsuranceDecision;
  const insuranceCrossCheckItems: DecisionMetadataItem[] = [
    {
      label: "Valerie plan",
      value: opsCrossCheck.primaryPlanLabel,
    },
    {
      label: "Valerie subscriber ID",
      value: opsCrossCheck.primarySubscriberId,
    },
    { label: "In network", value: inNetworkDisplay },
    { label: "Referral", value: referralRequirementDisplay },
    { label: "Authorization", value: authorizationRequirementDisplay },
  ];

  const insuranceIntakeDetails = coverageApproval?.metadata ? (
    <div className="space-y-3 rounded-md border p-3 text-sm">
      <DecisionMetadata
        items={[
          ...insuranceCrossCheckItems,
          { label: "Expected", value: coverageApproval.metadata.expectedCoverageCount },
          { label: "In Athena", value: coverageApproval.metadata.currentCoverages.length },
        ]}
      />
      <AthenaCoverageEvidence metadata={coverageApproval.metadata} />
    </div>
  ) : coverageReadback ? (
    <div className="space-y-3 text-sm">
      <DecisionMetadata
        items={[
          ...insuranceCrossCheckItems,
          { label: "In Athena", value: coverageReadback.coverages.length },
          { label: "Self pay", value: coverageReadback.primary.selfPay ? "Yes" : "No" },
        ]}
      />
      <div className="space-y-1">
        <p className="font-medium">Currently in Athena</p>
        {coverageReadback.coverages.length === 0 ? (
          <p>No coverage found.</p>
        ) : (
          coverageReadback.coverages.map((coverage) => (
            <p key={coverage.insuranceId}>
              <strong>
                {coverage.casePolicy
                  ? "Case policy"
                  : coverage.sequence === 1
                    ? "Primary"
                    : coverage.sequence === 2
                      ? "Secondary"
                      : `Sequence ${coverage.sequence ?? "N/A"}`}
                :
              </strong>{" "}
              {coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />
              {coverage.subscriberId ? (
                <>
                  {" "}
                  · Subscriber <CopyableContent text={coverage.subscriberId} />
                </>
              ) : null}
            </p>
          ))
        )}
      </div>
    </div>
  ) : followThroughEligible || assignmentStatus === "completed" ? (
    <DecisionMetadata items={insuranceCrossCheckItems} />
  ) : null;

  const steps: TimelineStep[] = [
    {
      number: 1,
      stepKey: TPR_TASK_STEP_KEY.LOCATE_DOCUMENT,
      title: "Patient Chart",
      status: patientChartStatus,
      summary: "Copy and search by the document ID in Referral.",
      preview: [
        { label: "Patient", value: opsCrossCheck.patientName },
        { label: "EHR ID", value: patientEhrId },
      ],
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: (
        <div className="space-y-3">
          <DecisionMetadata
            items={[
              {
                label: "Patient",
                value: (
                  <CopyableContent
                    text={`${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`}
                    textClassName="text-xs"
                  />
                ),
              },
              { label: "EHR ID", value: <CopyableContent text={patientEhrId} textClassName="text-xs" /> },
              { label: "Document ID", value: <CopyableContent text={documentId} textClassName="text-xs" /> },
              {
                label: "Home phone",
                value: <CopyableContent text={opsCrossCheck.patientHomePhoneNumber} textClassName="text-xs" />,
              },
              {
                label: "Mobile phone",
                value: <CopyableContent text={opsCrossCheck.patientMobilePhoneNumber} textClassName="text-xs" />,
              },
              {
                label: "Work phone",
                value: <CopyableContent text={opsCrossCheck.patientWorkPhoneNumber} textClassName="text-xs" />,
              },
              {
                label: "Email",
                value: <CopyableContent text={opsCrossCheck.patientEmail} textClassName="text-xs" />,
              },
              {
                label: "Fax",
                value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />,
              },
              { label: "Ethnicity", value: opsCrossCheck.patientEthnicity },
              { label: "Race", value: opsCrossCheck.patientRace },
              { label: "Language", value: opsCrossCheck.patientLanguage },
            ]}
          />
          {patientDisplayId ? (
            <Link
              href={`/patients/${patientDisplayId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              Open patient details
              <SquareArrowOutUpRight className="size-3.5" />
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      number: 2,
      stepKey: TPR_TASK_STEP_KEY.PROVIDER_LOCATION,
      title: "Receiving Provider and Location",
      status: assignmentConfirmationStatus,
      workflowStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      preview: [
        { label: "Patient type", value: patientLabel },
        { label: "Provider", value: resolvedReceiving.providerLabel },
        { label: "Location", value: resolvedReceiving.locationLabel },
      ],
      summary: usesRetiredUsualProviderRule
        ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history."
        : assignmentNeedsReview
          ? "Provider and location require manual review before downstream referral work."
          : "Verify we selected the correct receiving provider and location.",
      content: (
        <div className="space-y-3">
          <DecisionMetadata
            items={[
              { label: "Patient type", value: patientLabel },
              {
                label: "Provider",
                value: (
                  <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
                ),
              },
              {
                label: "NPI",
                value: (
                  <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
                ),
              },
              {
                label: "Location",
                value: (
                  <CopyableContent text={resolvedReceiving.locationLabel || NOT_AVAILABLE} textClassName="text-xs" />
                ),
              },
              {
                label: "Resolution",
                value: usesRetiredUsualProviderRule
                  ? "Verify appointment history"
                  : assignmentRequiresManualReview
                    ? "Manual review"
                    : "Confirmed",
              },
            ]}
          />
          {/* Gated rather than permanently visible: automation gets this right most of the time,
              and an always-open provider/location picker invites edits to assignments nobody found
              fault with — each of which replays a dozen downstream steps. See
              `providerAssignmentEditable` for what opens it. */}
          {providerAssignmentEditable ? (
            <TprReceivingAssignmentEditor
              task={task}
              currentProviderNpi={resolvedReceiving.providerNpi || null}
              currentDepartmentId={resolvedReceiving.locationEhrId || null}
            />
          ) : null}
          <ProviderSelectionChecklist
            evidence={providerSelectionEvidence}
            upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary}
            referralProviderName={opsCrossCheck.receivingProviderName}
            referralProviderNpi={opsCrossCheck.receivingProviderNpi}
            diagnosis={opsCrossCheck.diagnosis}
            diagnosisCategories={opsCrossCheck.diagnosisCategories}
            selectedProviderName={resolvedReceiving.providerLabel || NOT_AVAILABLE}
            selectedLocation={resolvedReceiving.locationLabel || NOT_AVAILABLE}
          />
          {resolvedReceiving.advisories.length > 0 ? (
            <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                {resolvedReceiving.advisories.map((advisory) => (
                  <li key={advisory}>{advisory}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {assignmentNeedsReview && resolvedReceiving.flags.length > 0 ? (
            <>
              <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                {resolvedReceiving.flags.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              {unresolvedUpcomingProviders.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3">
                  <span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>
                  {unresolvedUpcomingProviders.map((provider) => (
                    <Badge key={provider.providerEhrId ?? provider.label} variant="outline">
                      {provider.label}
                      {provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ),
      continuation: receivingAssignmentApproval
        ? {
            instruction: "After confirming the provider and location in Athena, continue the workflow.",
            control: <TprReceivingAssignmentContinueButton task={task} />,
          }
        : undefined,
    },
    {
      number: 3,
      stepKey: TPR_TASK_STEP_KEY.DEMOGRAPHICS_CARE_TEAM,
      title: "Demographics and Care Team",
      preview: [{ label: "PCP", value: pcpDisposition }],
      status: demographicsStatus,
      phase: hasEmergencyContactToEnter ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: hasEmergencyContactToEnter
        ? "Automation can't add the emergency contact. Enter it in Athena, then mark this updated."
        : undefined,
      workflowStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: hasEmergencyContactToEnter
        ? "Add the emergency contact to Athena by hand, then review patient and care team details in Quickview."
        : "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: (
        <div className="space-y-3">
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">Patient Details</p>
            <DecisionMetadata
              items={[
                { label: "Patient", value: opsCrossCheck.patientName },
                { label: "DOB", value: opsCrossCheck.patientDateOfBirth },
                { label: "Home phone", value: opsCrossCheck.patientHomePhoneNumber },
                { label: "Mobile phone", value: opsCrossCheck.patientMobilePhoneNumber },
                { label: "Work phone", value: opsCrossCheck.patientWorkPhoneNumber },
                { label: "Email", value: opsCrossCheck.patientEmail },
                { label: "Home address", value: opsCrossCheck.patientHomeAddress },
              ]}
            />
          </div>
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Care Team</p>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p>
              <DecisionMetadata
                items={
                  resolvedReceiving.usualProviderWriteSafe
                    ? [
                        {
                          label: "Provider",
                          value: (
                            <CopyableContent
                              text={resolvedReceiving.providerLabel || NOT_AVAILABLE}
                              textClassName="text-xs"
                            />
                          ),
                        },
                        {
                          label: "NPI",
                          value: (
                            <CopyableContent
                              text={resolvedReceiving.providerNpi || NOT_AVAILABLE}
                              textClassName="text-xs"
                            />
                          ),
                        },
                        { label: "Result", value: "Updated for new patient" },
                      ]
                    : [{ label: "Result", value: "Left unchanged for established patient" }]
                }
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p>
              <DecisionMetadata
                items={[
                  {
                    label: "Name",
                    value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />,
                  },
                  {
                    label: "NPI",
                    value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />,
                  },
                  {
                    label: "Address",
                    value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />,
                  },
                  { label: "Specialty", value: opsCrossCheck.referringProviderSpecialty },
                  /**
                   * Whether this provider also goes on the care team as the patient's PCP —
                   * `careTeamStep` adds the primary-care recipient class when intake checked
                   * "Is PCP", or when the specialty is primary care and they aren't already on the
                   * chart as PCP.
                   *
                   * Always shown, and always says which of the three cases applies. Previously a
                   * bare "PCP" group appeared only in the first case with no explanation, so a
                   * Family Medicine referrer who was already the PCP looked identical to one we had
                   * decided not to add — ops could not tell the difference.
                   */
                  { label: "Added as PCP", value: pcpDisposition },
                ]}
              />
            </div>
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">Emergency Contact</p>
            <DecisionMetadata
              items={[
                {
                  label: "Name",
                  value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />,
                },
                {
                  label: "Relationship",
                  value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />,
                },
                {
                  label: "Phone",
                  value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />,
                },
              ]}
            />
          </div>
          {demographicsReviewReasons.length ? (
            <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
              {demographicsReviewReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ),
    },
    {
      number: 4,
      stepKey: TPR_TASK_STEP_KEY.INSURANCE,
      title: "Insurance",
      preview: [
        { label: "Plan", value: opsCrossCheck.primaryPlanLabel },
        { label: "In network", value: insuranceRequirements ? inNetworkDisplay : null },
      ],
      status: eligibilityTimedOut ? "review_needed" : insuranceStatus,
      phase: coverageApproval || eligibilityTimedOut ? TaskStepPhase.ACTION_NEEDED : undefined,
      workflowStepKeys: [
        StepKey.CHECK_INSURANCE_ELIGIBILITY,
        StepKey.CREATE_COVERAGE,
        StepKey.CONFIRM_ATHENA_COVERAGE,
        StepKey.READ_ATHENA_COVERAGE,
        StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY,
        StepKey.SYNC_ATHENA_COVERAGES,
        StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS,
      ],
      summary: getTprInsuranceStepSummary({
        coverageApproval,
        isSelfPay: Boolean(coverageReadback?.primary.selfPay),
        hasInsuranceRequirements: Boolean(insuranceRequirements),
        inNetworkDisplay,
        insuranceStatusDisplay,
        coverageReadbackComplete,
        confirmedNonCamelbackAssignment,
        assignmentRequiresManualReview,
      }),
      content: (
        <div className="space-y-3">
          {/* `warn`, not `caution`: `caution` is now a pale cream and unreadable as body text. */}
          {athenaCoverageDrifted ? (
            <p className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-foreground">
              Athena&apos;s primary insurance has changed since this referral synced. The network and eligibility
              results below were decided against the coverage we wrote, not the one on the chart now — re-check them
              before relying on them.
            </p>
          ) : null}
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">{opsCrossCheck.primaryCoverageSlot.title}</p>
            <DecisionMetadata
              items={[
                {
                  label: "Plan (from Valerie)",
                  value: (
                    <span className="inline-flex items-center gap-1.5">
                      <CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />
                      {opsCrossCheck.isSpecialCaseInsurance ? (
                        <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">
                          Special Case Insurance
                        </Badge>
                      ) : null}
                    </span>
                  ),
                },
                {
                  label: "Subscriber ID (from Valerie)",
                  value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />,
                },
                {
                  label: "Policy holder",
                  value: <CopyableContent text={opsCrossCheck.primaryPolicyHolder} textClassName="text-xs" />,
                },
                {
                  label: "Plan (in Athena, live)",
                  value: <CopyableContent text={livePrimaryAthenaPlan} textClassName="text-xs" />,
                },
                // Reports whether the coverage is on the chart, not whether the read step ran.
                { label: "Synced to Athena", value: primaryCoverageInAthena ? "Yes" : "No" },
                {
                  label: "In network",
                  value: coverageReadback?.primary.selfPay ? "N/A — Self Pay" : inNetworkDisplay,
                },
              ]}
            />
            {coverageReadback && insuranceRequirements ? (
              <TprNetworkStatus
                task={task}
                steps={taskSteps}
                coverage={coverageReadback}
                requirements={insuranceRequirements}
              />
            ) : null}
            <DecisionMetadata
              items={[
                {
                  label: "Coverage eligibility",
                  value: followThroughEligible
                    ? insuranceStatusDisplay
                    : confirmedNonCamelbackAssignment
                      ? "Not applicable, non-Camelback location"
                      : skippedFollowThroughSummary,
                },
              ]}
            />
            {followThroughEligible && insuranceStatusDisplay === "Unverified" && !coverageReadback?.primary.selfPay ? (
              <p className="text-sm">Coverage eligibility still needs verification.</p>
            ) : null}
          </div>
          {/* Kept at this level rather than inside "More insurance detail": ops has to check the
              secondary plan on every referral that has one, and a collapsed panel gets skipped. */}
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">{opsCrossCheck.secondaryCoverageSlot.title}</p>
            {opsCrossCheck.secondaryPlanLabel || coverageReadback?.secondary || athenaCoverageLive.secondary ? (
              <DecisionMetadata
                items={[
                  {
                    label: "Plan (from Valerie)",
                    value: (
                      <span className="inline-flex items-center gap-1.5">
                        <CopyableContent
                          text={opsCrossCheck.secondaryPlanLabel || NOT_AVAILABLE}
                          textClassName="text-xs"
                        />
                        {opsCrossCheck.isSecondarySpecialCaseInsurance ? (
                          <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">
                            Special Case Insurance
                          </Badge>
                        ) : null}
                      </span>
                    ),
                  },
                  {
                    label: "Subscriber ID (from Valerie)",
                    value: (
                      <CopyableContent
                        text={opsCrossCheck.secondarySubscriberId || NOT_AVAILABLE}
                        textClassName="text-xs"
                      />
                    ),
                  },
                  {
                    label: "Policy holder",
                    value: (
                      <CopyableContent
                        text={opsCrossCheck.secondaryPolicyHolder || NOT_AVAILABLE}
                        textClassName="text-xs"
                      />
                    ),
                  },
                  {
                    label: "Plan (in Athena, live)",
                    value: <CopyableContent text={liveSecondaryAthenaPlan} textClassName="text-xs" />,
                  },
                  {
                    label: "Synced to Athena",
                    value: athenaCoverageLive.secondary || coverageReadback?.secondary ? "Yes" : "No",
                  },
                ]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No secondary insurance was captured from the referral or found in Athena.
              </p>
            )}
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">More insurance detail</summary>
            <div className="mt-3 space-y-3">
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-sm font-medium">Coverage</p>
                <DecisionMetadata
                  items={[{ label: "Self pay", value: coverageReadback?.primary.selfPay ? "Yes" : "No" }]}
                />
                <TprStediEligibilityValues referralId={referralId} coverage={coverage} />
                {insuranceIntakeDetails ? (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary>
                    <div className="mt-2">{insuranceIntakeDetails}</div>
                  </details>
                ) : null}
              </div>
              {insuranceRequirements?.reviewReasons.length ? (
                <div className="space-y-2 rounded-md border p-3">
                  <p className="text-sm font-medium">Network Review Reasons</p>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {insuranceRequirements.reviewReasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </details>
        </div>
      ),
      continuation: coverageApproval
        ? {
            instruction:
              "Choose each policy from Valerie or Athena, including its subscriber details, then sync to continue.",
            control: <AthenaCoverageResolutionDialog taskId={task.id} />,
          }
        : eligibilityTimedOut
          ? {
              instruction:
                "Athena did not return a complete eligibility response in time. Review coverage in Athena, then recheck eligibility.",
              control: (
                <TprManualStepContinueButton task={task} stepKey={StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY} />
              ),
            }
          : undefined,
    },
    {
      number: 5,
      stepKey: TPR_TASK_STEP_KEY.REFERRAL_AUTHORIZATION,
      title: "Referral Authorization",
      status: resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]),
      phase: referralAuthNeedsOutboundFax ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: referralAuthNeedsOutboundFax
        ? "This authorization needs an outbound fax, which isn't automated. Send it from Athena, then continue."
        : undefined,
      workflowStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary:
        opsKind === "pcp_referral_fax"
          ? (opsApproval?.metadata?.instruction ??
            "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax.")
          : opsKind === "authorization_review"
            ? (opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue.")
            : opsKind === "manual_authorization"
              ? (opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION)
              : referralAuthorization?.outcome === "created"
                ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.`
                : referralAuthorization?.outcome === "needs_pending_request"
                  ? "No authorization number is on file. An outbound request letter is required next."
                  : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements)
                    ? "No Athena object was created because the authorization requirements need manual review."
                    : referralAuthorization?.outcome === "not_needed"
                      ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).`
                      : followThroughEligible
                        ? "Waiting for network, authorization, and referral-requirement decisions."
                        : skippedFollowThroughSummary,
      content: (
        <div className="space-y-3">
          {insuranceRequirements ? null : (
            <p className="text-sm text-muted-foreground">
              {followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}
            </p>
          )}
          {insuranceRequirements && !showPcpReferralRequirement && !showReferralRequirement ? (
            <p className="text-sm text-muted-foreground">This plan does not require a referral or authorization.</p>
          ) : null}
          {showPcpReferralRequirement ? (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">PCP Referral Requirement</p>
              <DecisionMetadata
                items={[
                  { label: "PCP referral", value: "Required" },
                  { label: "Referring is PCP", value: isPrimaryCareProvider ? "Yes" : "No" },
                ]}
              />
            </div>
          ) : null}
          {showReferralRequirement ? (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Referral Requirement</p>
              <DecisionMetadata
                items={[
                  {
                    label: "Referral",
                    value: formatFromUnderscoreToHumanReadable(
                      insuranceRequirements?.referralRequirement ?? NOT_AVAILABLE
                    ),
                  },
                ]}
              />
              {showAuthorizationRequirement ? null : authorizationDetails}
            </div>
          ) : null}
          {showAuthorizationRequirement ? (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Authorization Requirement</p>
              <DecisionMetadata
                items={[
                  {
                    label: "Authorization",
                    value: formatFromUnderscoreToHumanReadable(
                      insuranceRequirements?.authorizationRequirement ?? NOT_AVAILABLE
                    ),
                  },
                ]}
              />
              {authorizationDetails}
            </div>
          ) : null}
          {/* With no requirement bubble to hold them, the write/upload controls still need a home
              while the requirements are unresolved. */}
          {insuranceRequirements ? null : (
            <TprSyncAuthButtons
              canUploadLetter={referralAuthNeedsPendingUpload}
              enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)}
              taskId={task.id}
            />
          )}
          {pcpFaxStatus === "review_needed" || authorizationStatus === "review_needed" ? (
            <details className="text-xs">
              <summary className="text-muted-foreground">How to complete this manually</summary>
              <div className="mt-2 space-y-3">
                <div>
                  <p className="font-medium text-foreground">Referral Required</p>
                  <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>
                      If a referral is required, confirm the received referral automatically created an object under the
                      Authorization section.
                    </li>
                    <li>
                      If it did not, manually create the referral object with:
                      <ul className="list-disc space-y-1 pl-5">
                        <li>Insurance / authorization number: always &quot;paper&quot;</li>
                        <li>Referral dates — default (if not provided): today through 1 year later</li>
                        <li>Number of visits — default (if not provided): 12 visits</li>
                        <li>ICD-10 codes</li>
                        <li>Referring provider</li>
                        <li>Specialty</li>
                        <li>Only if listed on the referral doc: receiving provider and department</li>
                      </ul>
                    </li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-foreground">Referral Authorization Required</p>
                  <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                    <li>
                      Same workflow as above, but enter the authorization number in the Insurance / authorization number
                      field.
                    </li>
                    <li>
                      Only send a fax if there is no authorization number. When sending an authorization request for a
                      missing auth number:
                      <ul className="list-disc space-y-1 pl-5">
                        <li>Manually export the doc to the referring provider via AthenaFax.</li>
                        <li>Add an authorization object with &quot;Pending&quot; in the auth number field.</li>
                        <li>
                          In all notes (patient notes, document internal note, action note): &quot;Referral
                          Authorization Requested [Date]&quot;
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </details>
          ) : null}
        </div>
      ),
      continuation:
        opsKind === "pcp_referral_fax"
          ? {
              instruction:
                opsApproval?.metadata?.instruction ??
                "After sending the outbound PCP referral fax from Athena, continue the workflow.",
              control: (
                <TprOpsContinueButton
                  task={task}
                  successTitle="PCP referral fax confirmed"
                  successDescription="The workflow is continuing with authorization."
                />
              ),
            }
          : opsKind === "authorization_review"
            ? {
                instruction:
                  opsApproval?.metadata?.instruction ??
                  "After reviewing the Athena referral authorization object, continue the workflow.",
                control: (
                  <TprOpsContinueButton
                    task={task}
                    successTitle="Authorization reviewed"
                    successDescription="The workflow is continuing with the remaining referral steps."
                  />
                ),
              }
            : opsKind === "manual_authorization"
              ? {
                  instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
                  control: (
                    <TprOpsContinueButton
                      task={task}
                      successTitle="Manual authorization recorded"
                      successDescription="The workflow is continuing with the remaining referral steps."
                    />
                  ),
                }
              : undefined,
    },
    {
      number: 6,
      stepKey: TPR_TASK_STEP_KEY.OUTBOUND_FAXES,
      detail: "Sending these faxes is still partly manual. Send them from Athena, then continue.",
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      workflowStepKeys: [
        StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM,
        StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD,
        StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX,
      ],
      summary:
        opsKind === "auth_request_upload"
          ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically."
          : outboundFaxStatus === "not_needed"
            ? "No outbound fax is required for this referral."
            : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: (
        <div className="space-y-3">
          {referralAuthNeedsPendingUpload || authRequestUploaded ? (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Authorization Request Letter</p>
              <DecisionMetadata
                items={[{ label: "Request letter", value: authRequestUploaded ? "Uploaded" : "Waiting for ops" }]}
              />
              {outboundFaxStatus === "review_needed" ? (
                <ol className="list-decimal space-y-1 pl-5 text-sm">
                  <li>Export the patient chart in Athena. Do not skip this export.</li>
                  <li>Click Continue below to upload the request letter. The letter is not uploaded automatically.</li>
                  <li>Athena then receives a Referral Auth object with number pending.</li>
                </ol>
              ) : null}
            </div>
          ) : null}
          {hasMissingInformation ? (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Missing Patient Phone Packet</p>
              <DecisionMetadata
                items={[
                  {
                    label: "Missing-info packet",
                    value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation",
                  },
                ]}
              />
              <p className="text-sm text-muted-foreground">
                Generated automatically when the patient phone number is missing — no upload needed here. Once it
                appears in the Athena chart, send it from Athena like any other outbound fax.
              </p>
            </div>
          ) : null}
          {outboundFaxStatus === "not_needed" ? (
            <p className="text-sm text-muted-foreground">
              Nothing to send — the referral authorization doesn&apos;t need an outbound request, and the patient phone
              number isn&apos;t missing.
            </p>
          ) : null}
        </div>
      ),
      continuation:
        opsKind === "auth_request_upload"
          ? {
              instruction: "After exporting the chart in Athena, click Continue to upload the request letter.",
              control: (
                <TprOpsContinueButton
                  task={task}
                  successTitle="Request letter queued"
                  successDescription="The workflow will write the pending authorization and upload the letter."
                />
              ),
            }
          : undefined,
    },
    {
      number: 7,
      stepKey: TPR_TASK_STEP_KEY.QUICKVIEW,
      title: "Quickview Note",
      // Writing the Quickview note is automated, so a failed disposition step is a failure of
      // this card, not a reason to grey it out. Otherwise it follows `dispositionStatus`, so a
      // pending disposition reads "Waiting" exactly like step 11.
      status: notesComplete ? "verification_required" : dispositionStatus,
      workflowStepKeys: [],
      summary: notesComplete
        ? "Confirm the final note appears correctly on the patient's Quickview tab in Athena."
        : "Waiting for the final note to be written before this can be verified.",
      preview: [{ label: "Expected Quickview note", value: internalNoteToEnter }],
      content: (
        <DecisionMetadata
          items={[
            {
              label: notesComplete
                ? "Confirm this appears in Quickview"
                : "Expected Quickview note (will be written by the workflow)",
              value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />,
            },
          ]}
        />
      ),
    },
    {
      number: 8,
      stepKey: TPR_TASK_STEP_KEY.FULL_REGISTRATION,
      title: "Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: (
        <div className="space-y-3">
          <DecisionMetadata
            items={[
              { label: "Calls", value: "Set to Yes only if blank — existing value is not overridden" },
              { label: "Texts", value: "Set to Yes only if blank — existing value is not overridden" },
              {
                label: "Privacy Notice",
                value:
                  privacyNoticeOnFile === null ? "Not read back" : privacyNoticeOnFile ? "Selected" : "Not selected",
              },
              {
                label: "How did you hear about us",
                // Same wording as Calls and Texts because it's the same policy: written only when
                // Athena's is blank. The read-back still drives the step's status — it just isn't
                // worth printing a raw source id at ops, who can read the real value in Athena.
                value: "Set to Physician Referral only if blank — existing value is not overridden",
              },
            ]}
          />
          {fullRegistrationReviewReasons.length ? (
            <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">
              {fullRegistrationReviewReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ),
    },
    {
      number: 9,
      stepKey: TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT,
      detail:
        "Parts of the referral document have to be keyed into Athena by hand. Enter them, then mark this updated.",
      title: "Referral Document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      workflowStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: (
        <div className="space-y-3">
          <DecisionMetadata
            items={[{ label: "Document ID", value: <CopyableContent text={documentId} textClassName="text-xs" /> }]}
          />
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Referral Fields</p>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                1. Referring Provider
              </p>
              <DecisionMetadata
                items={[
                  {
                    label: "Name",
                    value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />,
                  },
                  {
                    label: "NPI",
                    value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />,
                  },
                  {
                    label: "Address",
                    value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />,
                  },
                ]}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                2. New or Established Patient
              </p>
              <DecisionMetadata
                items={[
                  { label: "Patient type", value: <CopyableContent text={patientLabel} textClassName="text-xs" /> },
                ]}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Date Received</p>
              <DecisionMetadata
                items={[
                  {
                    label: "Date received",
                    value: <CopyableContent text={opsCrossCheck.dateReceived} textClassName="text-xs" />,
                  },
                ]}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Urgency</p>
              <DecisionMetadata
                items={[
                  { label: "Urgent", value: urgency.isUrgent ? "Yes" : "No" },
                  ...(urgency.reason ? [{ label: "Reason", value: urgency.reason }] : []),
                  { label: "Scheduling priority", value: urgency.schedulingPriority || NOT_AVAILABLE },
                ]}
              />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">5. Internal Note</p>
              <DecisionMetadata
                items={[
                  {
                    label: "Internal Note",
                    value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      number: 10,
      stepKey: TPR_TASK_STEP_KEY.DOCUMENT_UPLOAD,
      title: "Document Upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: !documentUploads.length
        ? "No documents were selected for upload."
        : resultDocumentsToLabel.length
          ? "Confirm the expected documents were uploaded to Athena, then add the document label to each lab and imaging result so it can be closed."
          : "Confirm the expected documents were uploaded to Athena.",
      workflowStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: (
        <div className="space-y-3">
          <DecisionMetadata
            items={[
              { label: "Documents", value: documentUploads.length },
              ...(resultDocumentsToLabel.length
                ? [{ label: "Need document label", value: resultDocumentsToLabel.length }]
                : []),
            ]}
          />
          <div className="space-y-1">
            <p className="text-sm font-medium">Expected Athena uploads</p>
            {documentUploads.length ? (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {documentUploads.map(({ label, resultDocumentLabel }) => (
                  <li key={label}>
                    <CopyableContent text={label} />
                    {resultDocumentLabel ? (
                      <div className="text-muted-foreground">
                        Add document label: <CopyableContent text={resultDocumentLabel} />
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>
            )}
          </div>
        </div>
      ),
    },
    {
      number: 11,
      stepKey: TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT,
      detail: "Choosing the bucket is ops' call. Assign it in Athena, then mark this updated.",
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([
        closeDocumentStatus,
        dispositionStatus === "failed"
          ? "not_run"
          : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete)
            ? "review_needed"
            : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]),
      ]),
      workflowStepKeys: [
        StepKey.TPR_CLOSE_DOCUMENT,
        StepKey.REFERRAL_DISPOSITION,
        StepKey.SCHEDULING_RECOMMENDATION,
        StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX,
      ],
      summary:
        opsKind === "close_document"
          ? (opsApproval?.metadata?.instruction ??
            "There is an upcoming appointment within 90 days. Close the document in Athena, then continue.")
          : hasMissingInformation && !missingInfoUploadComplete
            ? "Waiting for the missing-information document to be prepared."
            : referralAuthNeedsPendingUpload && authRequestUploaded
              ? "Confirm the authorization-request letter on the chart, route, and complete."
              : hasMissingInformation
                ? "Send the missing-information document, route the referral, and complete only after all manual actions are done."
                : "Confirm the recommended destination bucket — SS Cardio, Biltmore, or Close — and complete the task.",
      content: (
        <div className="space-y-3">
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">Bucket</p>
            <DecisionMetadata
              items={[
                {
                  label: "Suggested bucket",
                  value:
                    bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? (
                      NO_CONFIDENT_BUCKET_SUGGESTION
                    ) : bucketLabel === TprSyncBucket.CLOSED ? (
                      "Closed (status, not a bucket)"
                    ) : (
                      <CopyableContent text={bucketLabel} />
                    ),
                },
                ...(bucketLabel === TPR_MANUAL_REVIEW_BUCKET
                  ? []
                  : [{ label: "Why", value: getSuggestedBucketLabel(bucketLabel, closeReason) }]),
                { label: "Missing information", value: hasMissingInformation ? "Required" : "No" },
                {
                  label: "Authorization request",
                  value: authRequestUploaded ? "Uploaded" : "Not required",
                },
              ]}
            />
            {decisionTrace.length > 0 ? (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">How this was determined</summary>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                  {decisionTrace.map((entry, index) => (
                    <li key={`${entry.label}-${index}`}>
                      {entry.label} <strong>{entry.outcome}</strong>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
          {isReferralAuth ? <TprChartReferralAuthDocuments documents={chartReferralAuthDocuments} /> : null}
          {!resolvedReceiving.routedToSscardio &&
            !resolvedReceiving.isSpecialtyClinicReferral &&
            !workflowStillProcessing &&
            notesComplete &&
            task.type === TaskType.REFERRAL &&
            task.referral && (
              <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider />
            )}
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">Action Notes</p>
            <DecisionMetadata
              items={[{ label: "Action Notes", value: <CopyableContent text={actionNote} textClassName="text-xs" /> }]}
            />
          </div>
        </div>
      ),
      continuation:
        opsKind === "close_document"
          ? {
              instruction: "After closing the document in Athena, continue the workflow.",
              control: (
                <TprOpsContinueButton
                  task={task}
                  successTitle="Document close confirmed"
                  successDescription="The workflow is continuing with remaining follow-through."
                />
              ),
            }
          : // Completing the task is the bottom bar's job, not card 11's. Embedding it here meant
            // ruling on the last card completed the task outright; ops wanted an explicit Complete
            // after they've read the cards. See `hasEmbeddedVerificationActions` in `referral-view`.
            undefined,
    },
  ];
  /**
   * Steps that opt into `continueOnFailure` leave red rows the run went past, so several cards can
   * hold a failure. Each names its own earliest one, and that is the step its Continue settles. At
   * most one failure is the one the task is stopped on (see `isTprStoppedOnStep`); only that one
   * holds the queue and the cards behind it.
   */
  const failedTaskStepFor = (card: TimelineStep) =>
    taskSteps
      .filter((row) => row.status === TaskStepStatus.FAILED && card.workflowStepKeys.includes(row.stepKey as StepKey))
      .toSorted((left, right) => left.stepIndex - right.stepIndex)[0];
  const failureReasonOf = (row: TaskStep) =>
    row.output?.error || row.output?.failureReason || row.output?.ccErrors
      ? errorFromStepOutput(row.output, row.stepKey).message
      : null;
  const stoppedOnFailureCard = steps.find((card) => {
    const row = failedTaskStepFor(card);

    return row !== undefined && !isTprContinuedFailure(task.status, taskSteps, row);
  });
  /**
   * Each step paired with the phase its card will actually render in — same inputs, same
   * `resolveTaskStepPhase`, so the summary at the top can never label a step one way and the card
   * below another. The synthetic "failed" for a card holding a failed step mirrors
   * `TimelineStepCard`'s own `effectiveStatus`.
   *
   * Ops works the referral one step at a time, so only the earliest unresolved card presents a
   * phase: opening a task shows step 1 coloured and everything below it neutral, and confirming a
   * step is what brings the next one forward. Each step's result is what the next one is judged
   * against, so inviting ops to rule on step 7 before step 2 is settled asks them to sign off on
   * something the workflow may still redo.
   *
   * Two things move the sequence on. A confirmed step hands over to the next, and a step that was
   * never needed for this referral (`not_needed`) is skipped rather than waited on. A step the
   * workflow hasn't reached yet stops it, because nothing after it can be judged yet either.
   *
   * A failed sync is the exception and surfaces wherever it sits: it is a defect rather than a
   * queued spot-check, and burying it behind the queue would hide the thing holding the referral up.
   * Only the failure the task is stopped on holds the queue; one the run went past doesn't.
   */
  let sequenceOpen = true;
  let sequenceBlockedBy: TimelineStep | null = null;
  const phasedSteps = steps.map((step) => {
    const offersManualBypass = Boolean(manualBypassStepKey && step.workflowStepKeys.includes(manualBypassStepKey));
    // A step may pin its own phase per-referral (step 5 does, on whether a fax is needed); the
    // static map covers the ones that are always a human's job.
    const pinnedPhase = offersManualBypass
      ? TaskStepPhase.ACTION_NEEDED
      : (step.phase ?? TPR_PINNED_STEP_PHASE[step.stepKey]);
    const failedTaskStep = failedTaskStepFor(step);
    const isFailedSync = failedTaskStep !== undefined;
    const continuedPastFailure =
      failedTaskStep !== undefined && isTprContinuedFailure(task.status, taskSteps, failedTaskStep);
    const status = isFailedSync ? "failed" : offersManualBypass ? "review_needed" : step.status;
    const naturalPhase = resolveTaskStepPhase(status, pinnedPhase);
    const verdict = verdictByStepKey.get(step.stepKey)?.verdict;
    /**
     * The card the workflow is actually parked on, which surfaces wherever it sits — the same
     * exception a failed sync gets, for the same reason.
     *
     * Every `continuation` above is conditioned on a pending approval, so that is normally exactly
     * "the card holding the gate". Queuing it behind the cards above it was a deadlock, not a delay:
     * the run cannot advance until ops releases the gate, and the release control only renders on
     * a presented card. Ops saw a grey card with no button on the one step that needed them, and
     * nothing they could do to the cards above it moved the workflow — those are waiting on this.
     *
     * Marking an earlier card Incorrect is the common way in. That closes the queue on purpose
     * (ops is fixing it), and it used to hide the gate they have to clear to apply the fix.
     *
     * The approval's own `stepKey` is the second, more reliable route to the same card, and it is
     * what covers the gate whose metadata this build can't read. Every continuation above is keyed
     * on a parsed discriminator (`opsKind`), so a row storing a kind this build doesn't know parses
     * to `metadata: null`, no card offers a continuation, and the referral presents as if nothing
     * were wrong. Surfacing the owning card regardless at least names the step ops is held at.
     */
    const ownsPendingGate =
      step.continuation !== undefined ||
      (pendingApproval !== null &&
        pendingApproval.stepKey !== null &&
        step.workflowStepKeys.includes(pendingApproval.stepKey));
    const confirmed =
      !isFailedSync && !ownsPendingGate && !offersManualBypass && verdict === VerificationResult.CORRECT;
    /**
     * Only when every step on the card has run and this is its only failure: signing off earlier
     * would confirm results ops hasn't seen yet. A card ops marked Incorrect keeps that verdict.
     */
    const signOffOnContinue =
      continuedPastFailure &&
      verdict !== VerificationResult.INCORRECT &&
      step.workflowStepKeys.every((stepKey) =>
        taskSteps.some(
          (row) => row.stepKey === stepKey && (row.status === TaskStepStatus.COMPLETED || row === failedTaskStep)
        )
      );
    // The Quickview card shows the note it expects before the workflow writes it. Until then its
    // phase is null, so presenting it early is read-only and offers no verdict.
    const readOnlyEarlyPreview = step.stepKey === TPR_TASK_STEP_KEY.QUICKVIEW && naturalPhase === null;
    const presented = sequenceOpen || isFailedSync || ownsPendingGate || offersManualBypass || readOnlyEarlyPreview;
    const phase = presented ? naturalPhase : null;
    const awaitingStep = presented ? null : sequenceBlockedBy;

    // A failure the run went past stays red wherever it sits but doesn't hold the cards after it.
    if (
      sequenceOpen &&
      !continuedPastFailure &&
      step.status !== "not_needed" &&
      !(naturalPhase !== null && confirmed)
    ) {
      sequenceOpen = false;
      sequenceBlockedBy = step;
    }

    return {
      step: { ...step, status, phase: pinnedPhase },
      failedStepKey: (failedTaskStep?.stepKey as StepKey | undefined) ?? null,
      failureReason: failedTaskStep ? failureReasonOf(failedTaskStep) : null,
      continuedPastFailure,
      signOffOnContinue,
      phase,
      /** Null only because a later card is waiting its turn, as opposed to not having run. */
      awaitingStep,
      /** The phase this card would present in on its own, used to tell those two cases apart. */
      naturalPhase,
    };
  });
  /**
   * The one page-level line left above the timeline.
   *
   * The summary card that used to sit here enumerated every outstanding step, which only made sense
   * while all eleven presented at once — ops now works them in order, so it restated the single
   * active card and nothing else. What it also carried, and the cards can't, is the three states
   * where no card has anything to say: a referral holding for its pair, a run still in flight, and
   * a finished one. All eleven cards are neutral in each, so without this the page would be blank.
   */
  const timelineNotice = waitingForAssociatedReferral
    ? "Waiting for the matching referral for this patient to finish syncing. This authorization continues once it does, or after two business days."
    : phasedSteps.some((entry) => entry.phase !== null)
      ? null
      : workflowStillProcessing
        ? "Automation is still running. This timeline will update as each durable workflow step completes."
        : // Never claim a parked run is finished. A pending approval means the workflow stopped and
          // is waiting on a human, so "No manual actions remain" reads as "nothing to do here" on
          // precisely the tasks that need ops most — which is how gated referrals sat for weeks and
          // were closed out as duplicates.
          //
          // Reachable when no card can offer a control for the gate: a legacy row whose metadata
          // this build can't parse has no `opsKind`, so nothing renders a continuation. Refreshing
          // does not help with that, and telling ops to refresh sends them round a loop — the line
          // says plainly that the referral needs engineering instead.
          pendingApproval
          ? "This referral is held at an approval no step can resolve. It needs engineering — escalate rather than closing it out."
          : "No manual actions remain.";

  return (
    <div className="space-y-8 pb-8">
      {isReferralAuth ? (
        <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">
          <p className="font-medium">Referral Authorization</p>
          <p>This document is a standalone referral authorization, not a referral.</p>
        </div>
      ) : null}
      {priorAuthorizationDocumentId ? (
        <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">
          <p className="font-medium">Referral Auth Already Processed</p>
          <p>
            Valerie already processed this referral from referral authorization doc ID #{priorAuthorizationDocumentId}.
            Close this document instead of working it as a new referral.
          </p>
        </div>
      ) : null}
      {timelineNotice ? <p className="text-sm text-muted-foreground">{timelineNotice}</p> : null}
      {phasedSteps.map(
        ({
          step,
          naturalPhase,
          awaitingStep,
          failedStepKey,
          failureReason,
          continuedPastFailure,
          signOffOnContinue,
        }) => (
          <TimelineStepCard
            key={step.number}
            task={task}
            step={step}
            failedStepKey={failedStepKey}
            bypassStepKey={
              manualBypassStepKey && step.workflowStepKeys.includes(manualBypassStepKey) ? manualBypassStepKey : null
            }
            failureReason={failureReason}
            continuedPastFailure={continuedPastFailure}
            signOffOnContinue={signOffOnContinue}
            // The failure the task is stopped on halts the workflow, so anything that genuinely hasn't run is stuck behind
            // it. Keyed on `naturalPhase`, not the sequenced one: a card merely waiting its turn is
            // not blocked by the failure, and saying so would name the wrong cause. Worth naming at
            // all because the cards are numbered in reading order rather than execution order — step
            // 1 waits on step 2 here, since the receiving assignment resolves before the chart.
            blockedBy={
              naturalPhase === null && stoppedOnFailureCard && stoppedOnFailureCard.number !== step.number
                ? stoppedOnFailureCard
                : null
            }
            awaitingStep={awaitingStep}
          />
        )
      )}
    </div>
  );
}

interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  referringProviderMarkedPcp: boolean;
  /** Whether any member of the Athena care team is already PRIMARY_CARE_PROVIDER — suppresses
   * adding a second PCP in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's values: "closed" | "sscardio" | "referralscardiology" | "biltmorecardreferral" | "biltmorecardiosched" | "biltmorecoumadin" | "pulmonaryreferrals" */
  bucketLabel: string;
  closeReason: string | null;
  actionNote: string;
  /** Athena document priority (Urgent vs Normal) and why — shown in Step 9 before the internal note. */
  urgency: TprSyncChecklistUrgency;
  /** The note the disposition step will write, always carrying the
   * "Reviewed By Valerie Health (date) - <provider> - ..." prefix. Step 9 shows this rather than the
   * Athena read-back, which is still empty at that point in the workflow. */
  expectedInternalNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploads: TprDocumentUpload[];
  privacyNoticeOnFile: boolean | null;
  /** Athena's current `referralsourceid`, or null when blank. */
  referralSourceOnFile: string | null | undefined;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    /** Athena department id behind `locationLabel` — preselects the ops location picker. */
    locationEhrId: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    isSpecialtyClinicReferral?: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
  isReferralAuth: boolean;
  priorAuthorizationDocumentId: string | null;
  chartReferralAuthDocuments: TprChartDocument[] | null;
  linkedPriorAuthorization: PriorAuthorization | null;
}

export function TprReferralInstructions({
  view,
  referral,
  task,
  athenaData,
  patientEhrId,
  patientLabel,
  upcomingAppointments,
  pastAppointments,
  documentId,
  isPrimaryCareProvider,
  referringProviderMarkedPcp,
  primaryCareProviderAlreadyPresent,
  bucketLabel,
  closeReason,
  actionNote,
  urgency,
  expectedInternalNote,
  decisionTrace,
  flags,
  eligibilityDisplay,
  documentUploads,
  privacyNoticeOnFile,
  referralSourceOnFile,
  taskSteps,
  pendingApproval,
  receivingAssignmentConfirmed,
  followThroughEligible,
  coverageReadback,
  insuranceRequirements,
  referralAuthorization,
  unresolvedUpcomingProviders,
  upcomingAppointmentProviderSummary,
  resolvedReceiving,
  chartReferralAuths,
  chartRequestLetters,
  isReferralAuth,
  priorAuthorizationDocumentId,
  chartReferralAuthDocuments,
  linkedPriorAuthorization,
}: TprReferralInstructionsProps) {
  const referringLocationAddress = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  const referringProviderName = referral.referringProvider
    ? getPractitionerNameWithCredentials(referral.referringProvider)
    : NOT_AVAILABLE;
  const receivingProviderName = referral.receivingProvider
    ? getPractitionerNameWithCredentials(referral.receivingProvider)
    : NOT_AVAILABLE;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  const primaryCoverageSummary = getCoverageSummary(referral.primaryCoverage);
  const secondaryCoverageSummary = getCoverageSummary(referral.secondaryCoverage);
  const coverageSlots = getTprCoverageSlots(referral.primaryCoverage, referral.secondaryCoverage);
  const dateReceived = formatDateMmDdYyyy(referral.dateReceived) || NOT_AVAILABLE;
  // "referralInformation.schedulingPriority" in blocks.yaml is the block-field path; the actual
  // Referral model field is the flat `schedulingPriority`.
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  // The real Athena-synced internal note. The "Document Information" card lower on the page
  // (AthenaReferralCards) reads the same `athenaData.document.internalNote`, so that card keeps
  // showing whatever Athena actually holds right now — including nothing.
  const athenaInternalNote = athenaData?.document?.internalNote || NOT_AVAILABLE;
  // What every "enter this note in Athena" instruction should show — both the Step 9 card in the
  // steps view and the reference checklist's Internal Note / quickview lines in the information
  // view. Athena's own note is empty (or holds only `careTeamStep`'s address-mismatch note) until
  // the disposition step runs, which is long after a reviewer is asked to enter it, so fall back to
  // the previewed note. Deliberately diverges from the Document Information card above: that card
  // reports Athena's current state, while this tells the reviewer what to type.
  const internalNoteToEnter = athenaInternalNote.toLowerCase().includes(REVIEWED_BY_VALERIE_HEALTH_MARKER)
    ? athenaInternalNote
    : expectedInternalNote;
  const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
  const syncFailed = task.status === TaskStatus.FAILED;

  // Patient Chart Create Step 6: "Missing Insurance" is its own state, distinct from the
  // eligibility result — there's nothing to check eligibility on at all if no coverage was extracted.
  const insuranceStatusDisplay = coverageReadback?.primary.selfPay
    ? "Self Pay"
    : referral.primaryCoverage
      ? eligibilityDisplay
      : "Missing Insurance";

  // Missing Information Step 1/2: only a missing phone on a Camelback referral requires a letter.
  // The phone only counts as missing if it's absent from BOTH the referral extraction and the
  // patient's existing Athena chart record. Insurance and address remain visible in the data-source
  // checklist, but neither one generates a missing-information letter.
  const chartPatient: Patient | null = athenaData?.patientDetails ?? null;
  const referralPhone = (patient && getPatientPhoneNumber(patient)?.trim()) || null;
  const chartPhone = (chartPatient && getPatientPhoneNumber(chartPatient)?.trim()) || null;
  // Shown one row per number rather than the single `getPatientPhoneNumber` pick, which returns
  // home or mobile or work and hides whichever it didn't choose. Each contact row shows what the
  // chart should hold once the patient write has run, so ops can confirm it landed: the write in
  // `updateAthenaPatient` overrides every phone number and the email with the referral's value.
  // Where the referral carried nothing, the chart's existing value is what stays on file.
  const homePhone = patient?.homePhoneNumber?.trim() || chartPatient?.homePhoneNumber?.trim() || null;
  const mobilePhone = patient?.mobilePhoneNumber?.trim() || chartPatient?.mobilePhoneNumber?.trim() || null;
  const workPhone = patient?.workPhoneNumber?.trim() || chartPatient?.workPhoneNumber?.trim() || null;
  const email =
    patient?.personalEmail?.trim().toLowerCase() || chartPatient?.personalEmail?.trim().toLowerCase() || null;
  const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
  // Athena coverages aren't necessarily returned in primary-first order — sort by `order` (1 = primary)
  // the same way AthenaReferralCards does, so this doesn't accidentally pick up a secondary plan.
  const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99))[0];
  const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
  const referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
  const chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
  // The write overrides the chart's address only with a complete one; a partial address is never
  // sent, so the chart keeps its own (and the sync-gap check below flags it).
  const syncedAddressValue = isCompleteAddress(patient?.homeAddress)
    ? referralAddressValue
    : (chartAddressValue ?? referralAddressValue);

  /**
   * Contact details automation was supposed to have written to Athena but that aren't on the
   * chart — step 3 fails on these so ops keys them in. Read from the same live chart the cards
   * display, so the flag clears itself on the next refresh once the value is in Athena.
   */
  const patientContactSyncGaps = patient && chartPatient ? findPatientContactSyncGaps(patient, chartPatient) : [];
  const phoneDataSourceEntry = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
  const dataSourceEntries: DataSourceEntry[] = [
    phoneDataSourceEntry,
    resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue),
    resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue),
  ];
  const isCamelbackReferral = !resolvedReceiving.routedToSscardio && !resolvedReceiving.isSpecialtyClinicReferral;
  const missingFields = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
  const hasMissingInformation = missingFields.length > 0;
  const patientDisplayName = getPatientDisplayName(patient);
  const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
  const diagnosis =
    [
      referral.reasonForReferral,
      ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map((item) => item.diagnosisText),
    ]
      .filter((value): value is string => Boolean(value))
      .filter((value, index, values) => values.indexOf(value) === index)
      .join(", ") || NOT_AVAILABLE;
  // Recomputed from the live referral rather than read back off the persisted assignment, for the
  // same reason `diagnosis` above is — the category the reviewer sees then always agrees with the
  // diagnosis text shown next to it, even if the extraction was edited after the workflow ran.
  const diagnosisCategories = describeTprSpecialtyCategories([matchTprDiagnosisSpecialties(referral).primary]);
  const authorizationStatus = linkedPriorAuthorization?.authorizationStatus ?? referral.authorizationStatus;
  const opsCrossCheck: TprOpsCrossCheck = {
    patientName: patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE,
    patientDateOfBirth: formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE,
    patientHomePhoneNumber: homePhone ?? NOT_AVAILABLE,
    patientMobilePhoneNumber: mobilePhone ?? NOT_AVAILABLE,
    patientWorkPhoneNumber: workPhone ?? NOT_AVAILABLE,
    patientFaxNumber: (patient && getPatientFaxNumber(patient)) || NOT_AVAILABLE,
    patientEthnicity: patient?.ethnicity || NOT_AVAILABLE,
    patientRace: patient?.race || NOT_AVAILABLE,
    patientLanguage: patient?.language || NOT_AVAILABLE,
    patientEmail: email ?? NOT_AVAILABLE,
    patientHomeAddress: syncedAddressValue || NOT_AVAILABLE,
    receivingProviderName,
    receivingProviderNpi: referral.receivingProvider?.npiNumber || NOT_AVAILABLE,
    referringProviderName,
    referringProviderNpi: referral.referringProvider?.npiNumber || NOT_AVAILABLE,
    referringProviderSpecialty,
    referringLocationName: referral.referringLocation?.name || NOT_AVAILABLE,
    referringLocationAddress,
    emergencyContactName,
    emergencyContactPhoneNumber,
    emergencyContactRelationship,
    dateReceived,
    primaryPlanLabel: referral.primaryCoverage
      ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false,
        })
      : "Self Pay",
    primarySubscriberId: referral.primaryCoverage?.subscriberId || NOT_AVAILABLE,
    primaryPolicyHolder: getPolicyHolder(referral.primaryCoverage) || NOT_AVAILABLE,
    isSpecialCaseInsurance: isTprSpecialCaseInsurance(referral.primaryCoverage?.payer),
    primaryCoverageSlot: coverageSlots.primary,
    secondaryCoverageSlot: coverageSlots.secondary,
    // Null rather than "N/A" so the Insurance step can tell "the patient has no secondary plan"
    // apart from "there is a secondary plan but we captured nothing for it".
    secondaryPlanLabel: referral.secondaryCoverage
      ? getCoverageDisplayName({
          coverage: referral.secondaryCoverage,
          includesPlan: true,
          includeSubscriberName: false,
        })
      : null,
    secondarySubscriberId: referral.secondaryCoverage?.subscriberId || null,
    secondaryPolicyHolder: getPolicyHolder(referral.secondaryCoverage),
    isSecondarySpecialCaseInsurance: isTprSpecialCaseInsurance(referral.secondaryCoverage?.payer),
    authorizationNumber: referral.authorizationNumber?.trim() || NOT_AVAILABLE,
    authorizationStatus: authorizationStatus ? formatFromUnderscoreToHumanReadable(authorizationStatus) : NOT_AVAILABLE,
    authorizationStartDate:
      formatDateMmDdYyyy(linkedPriorAuthorization?.startDate ?? referral.startDate) || NOT_AVAILABLE,
    authorizationEndDate: formatDateMmDdYyyy(linkedPriorAuthorization?.endDate ?? referral.endDate) || NOT_AVAILABLE,
    authorizationVisits: referral.numberOfVisits?.toString() ?? NOT_AVAILABLE,
    authorizationServices:
      (linkedPriorAuthorization?.numberOfServices ?? referral.numberOfServices)?.toString() ?? null,
    diagnosis,
    diagnosisCategories,
  };
  /**
   * Athena's coverages as of *this render*, not as of when the workflow ran. `coverageReadback` is a
   * persisted step output frozen at sync time, so when ops fixes insurance directly in Athena the
   * step used to keep showing the stale snapshot. Same primary/secondary ordering convention as
   * `AthenaReferralCards`.
   */
  const sortedAthenaCoverages = [...(athenaData?.coverages ?? [])].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const athenaCoverageLive = {
    primary: sortedAthenaCoverages.find((coverage) => coverage.order === 1) ?? sortedAthenaCoverages[0] ?? null,
    secondary: sortedAthenaCoverages.find((coverage) => coverage.order === 2) ?? sortedAthenaCoverages[1] ?? null,
  };

  if (view === "steps") {
    return (
      <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto">
        <TprSyncTimeline
          task={task}
          referralId={referral.id}
          coverage={referral.primaryCoverage}
          taskSteps={taskSteps}
          pendingApproval={pendingApproval}
          receivingAssignmentConfirmed={receivingAssignmentConfirmed}
          followThroughEligible={followThroughEligible}
          coverageReadback={coverageReadback}
          athenaCoverageLive={athenaCoverageLive}
          insuranceRequirements={insuranceRequirements}
          referralAuthorization={referralAuthorization}
          resolvedReceiving={resolvedReceiving}
          patientLabel={patientLabel}
          patientEhrId={patientEhrId}
          documentId={documentId}
          bucketLabel={bucketLabel}
          closeReason={closeReason}
          internalNoteToEnter={internalNoteToEnter}
          urgency={urgency}
          actionNote={actionNote}
          decisionTrace={decisionTrace}
          insuranceStatusDisplay={insuranceStatusDisplay}
          hasMissingInformation={hasMissingInformation}
          unresolvedUpcomingProviders={unresolvedUpcomingProviders}
          upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary}
          upcomingAppointments={upcomingAppointments}
          pastAppointments={pastAppointments}
          documentUploads={documentUploads}
          privacyNoticeOnFile={privacyNoticeOnFile}
          referralSourceOnFile={referralSourceOnFile}
          isPrimaryCareProvider={isPrimaryCareProvider}
          referringProviderMarkedPcp={referringProviderMarkedPcp}
          primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent}
          patientDisplayId={patient.displayId}
          opsCrossCheck={opsCrossCheck}
          patientContactSyncGaps={patientContactSyncGaps}
          chartReferralAuths={chartReferralAuths}
          chartRequestLetters={chartRequestLetters}
          isReferralAuth={isReferralAuth}
          priorAuthorizationDocumentId={priorAuthorizationDocumentId}
          chartReferralAuthDocuments={chartReferralAuthDocuments}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto">
      <EhrInstructionsCard collapsibleTitle="Detailed Athena verification reference">
        <div className="text-sm text-muted-foreground space-y-6">
          <h3 className="text-md font-semibold text-center text-foreground">
            {isReferralAuth ? "Referral Authorization" : "TPR Biltmore Cardiology Sync Verification"}
          </h3>
          <p className="text-xs text-center">
            {syncFailed
              ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing."
              : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing."}
          </p>
          <SyncIncompleteActions taskId={task.id} />

          <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">
            <p>
              <strong>Camelback Referral:</strong> {isCamelbackReferral ? "Yes" : "No"}
            </p>
            <p>
              <strong>Patient:</strong> <CopyableContent text={patientDisplayName} /> ({patientLabel})
            </p>
            <p>
              <strong>Receiving Provider (as extracted):</strong>{" "}
              <ProviderCopyFields provider={referral.receivingProvider} />, <strong>Location:</strong>{" "}
              <CopyableContent text={receivingLocationName} />
            </p>
          </div>

          <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">
            <p className="font-semibold">Recommended Receiving Provider / Location</p>
            <p>
              <strong>Provider:</strong> <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} />,{" "}
              <strong>Location:</strong> <CopyableContent text={resolvedReceiving.locationLabel || NOT_AVAILABLE} />
            </p>
            {resolvedReceiving.routedToSscardio && !resolvedReceiving.isSpecialtyClinicReferral && (
              <p className="text-xs">
                Routing:{" "}
                <strong>
                  {bucketLabel === TprSyncBucket.SS_CARDIO || bucketLabel === TprSyncBucket.REFERRALS_CARDIOLOGY
                    ? bucketLabel
                    : resolveTprNonCamelbackBucket({
                        departmentId: resolvedReceiving.locationEhrId,
                        providerNpi: resolvedReceiving.providerNpi,
                      })}
                </strong>
              </p>
            )}
            {resolvedReceiving.flags.length > 0 && (
              <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">
                {resolvedReceiving.flags.map((flag, index) => (
                  <li key={index}>{flag}</li>
                ))}
              </ul>
            )}
            {resolvedReceiving.trace.length > 0 && (
              <details className="text-xs">
                <summary>Why</summary>
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  {resolvedReceiving.trace.map((entry, index) => (
                    <li key={index}>{entry}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>
            <ol className="list-decimal list-inside space-y-3">
              <li>
                Search by document ID: <CopyableContent text={documentId} />
              </li>
              <li>
                Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} />
              </li>
              <li>Hit save and proceed to quickview.</li>
              <li>
                Check the Patient Care Team:
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>
                    Usual Provider:{" "}
                    {resolvedReceiving.usualProviderWriteSafe ? (
                      <ResolvedProviderCopyFields
                        providerLabel={resolvedReceiving.providerLabel}
                        providerNpi={resolvedReceiving.providerNpi}
                        writeSafe
                      />
                    ) : (
                      "Leave unchanged"
                    )}
                    <p className="text-xs italic mt-1">
                      {resolvedReceiving.usualProviderWriteSafe
                        ? "New patient — add the usual provider to the care team."
                        : "Established patient — left unchanged to preserve continuity of care."}
                    </p>
                  </li>
                  <li>
                    Referring Provider: <ProviderCopyFields provider={referral.referringProvider} />, Specialty:{" "}
                    <CopyableContent text={referringProviderSpecialty} />
                  </li>
                  {(isPrimaryCareProvider || referringProviderMarkedPcp) && (
                    <li>
                      PCP: <ProviderCopyFields provider={referral.referringProvider} />
                    </li>
                  )}
                  {referral.referringProvider && (
                    <li>
                      Referring provider address: <CopyableContent text={referringLocationAddress} />
                    </li>
                  )}
                </ul>
              </li>
              <li>
                Check the Emergency Contact
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>
                    Name: <CopyableContent text={emergencyContactName} />
                  </li>
                  <li>
                    Phone Number: <CopyableContent text={emergencyContactPhoneNumber} />
                  </li>
                  <li>
                    Relationship to Patient: <CopyableContent text={emergencyContactRelationship} />
                  </li>
                </ul>
              </li>
              <li>
                Check the patient insurance
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>
                    {coverageSlots.primary.label}: <CopyableContent text={primaryCoverageSummary ?? "N/A"} />
                  </li>
                  {secondaryCoverageSummary && (
                    <li>
                      {coverageSlots.secondary.label}: <CopyableContent text={secondaryCoverageSummary} />
                    </li>
                  )}
                  <li>
                    Eligibility check result: <CopyableContent text={insuranceStatusDisplay} />
                    {insuranceStatusDisplay === "Missing Insurance" && (
                      <p className="text-xs italic mt-1">
                        Ops needs to call the referring provider to verify insurance.
                      </p>
                    )}
                    {insuranceStatusDisplay === "Unverified" && (
                      <p className="text-xs italic mt-1">
                        Ops needs to call the referring provider to verify insurance.
                      </p>
                    )}
                  </li>
                </ul>
              </li>
              <li>
                Go to full registration and verify:
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>Call and text are YES</li>
                  <li>
                    Privacy is YES
                    {privacyNoticeOnFile !== true && (
                      <p className="text-xs italic mt-1">
                        {privacyNoticeOnFile === false
                          ? "Privacy is not set on the Athena chart. Set it to YES during review."
                          : "Could not verify Athena privacy. Confirm it is YES during review."}
                      </p>
                    )}
                  </li>
                  <li>
                    If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value
                    unchanged.
                  </li>
                </ul>
              </li>
            </ol>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>
            <ol className="list-decimal list-inside space-y-3">
              <li>
                Search by document ID: <CopyableContent text={documentId} />
              </li>
              <li>
                Referring provider: Name: <CopyableContent text={referringProviderName} />, NPI:{" "}
                <CopyableContent text={referral.referringProvider?.npiNumber || NOT_AVAILABLE} />, Address:{" "}
                <CopyableContent text={referringLocationAddress} />
              </li>
              <li>
                Receiving provider:{" "}
                <ResolvedProviderCopyFields
                  providerLabel={resolvedReceiving.providerLabel}
                  providerNpi={resolvedReceiving.providerNpi}
                  writeSafe={resolvedReceiving.providerWriteSafe}
                />
              </li>
              <li>
                Patient Type: <CopyableContent text={patientLabel} />
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">
                  {sortedPastAppointments.length === 0 ? (
                    <li>No appointment history found</li>
                  ) : (
                    sortedPastAppointments.map((appointment) => (
                      <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>
                    ))
                  )}
                </ul>
              </li>
              <li>
                Date: <CopyableContent text={dateReceived} />
              </li>
              <li>
                Internal Note: <CopyableContent text={internalNoteToEnter} />
                {flags.length > 0 && (
                  <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">
                    {flags.map((flag, index) => (
                      <li key={index}>{flag}</li>
                    ))}
                  </ul>
                )}
              </li>
              <li>
                Urgency: <CopyableContent text={schedulingPriority} />
              </li>
              <li>
                {bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? (
                  <>{NO_CONFIDENT_BUCKET_SUGGESTION}</>
                ) : (
                  <>
                    Suggested Bucket: <CopyableContent text={getSuggestedBucketLabel(bucketLabel, closeReason)} />
                  </>
                )}
                {decisionTrace.length > 0 &&
                  (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? (
                    <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">
                      {decisionTrace.map((entry, index) => (
                        <li key={`${entry.label}-${index}`}>
                          {entry.label} <strong>{entry.outcome}</strong>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">
                      <li>
                        {decisionTrace[decisionTrace.length - 1].label}{" "}
                        <strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong>
                      </li>
                    </ul>
                  ))}
              </li>
              <li>
                Action note: <CopyableContent text={actionNote} />
              </li>
              <li>
                Go to quickview and add the internal note: <CopyableContent text={internalNoteToEnter} />
              </li>
            </ol>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>
            <p className="mb-1">
              Verify the following documents were uploaded, and add the listed document label to each lab and imaging
              result so it can be closed:
            </p>
            <ul className="ml-4 space-y-1 list-disc list-inside">
              {documentUploads.length === 0 ? (
                <li>N/A</li>
              ) : (
                documentUploads.map(({ label, resultDocumentLabel }) => (
                  <li key={label}>
                    <CopyableContent text={label} />
                    {resultDocumentLabel ? (
                      <>
                        {" "}
                        — Add document label: <CopyableContent text={resultDocumentLabel} />
                      </>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>

            <p className="font-medium text-foreground">Step 1: Missing Information Check</p>
            <p className="mb-3">
              {hasMissingInformation
                ? `Missing: ${missingFields.map((entry) => entry.label).join(", ")}`
                : "No missing-information letter needed."}
            </p>

            <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>
            <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">
              {dataSourceEntries.map((entry) => (
                <li key={entry.label}>
                  {entry.label}: <CopyableContent text={entry.value} /> ({entry.source})
                </li>
              ))}
            </ul>

            {hasMissingInformation && (
              <ol className="list-decimal list-inside space-y-2">
                <li>Verify the Auto-Generated document was added to the patient chart</li>
                <li>
                  Send the document out.
                  {/* Manual "a./c./d." labels (skipping "b.") to match ops' SOP verbatim — an <ol type="a">
                      would auto-letter sequentially and couldn't reproduce the intentional gap. */}
                  <ul className="ml-4 mt-1 space-y-2 list-none">
                    <li>
                      a. Chart Export - sending to referring provider
                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                        <li>Click the hamburger menu (3 dashes) → select Chart Export.</li>
                        <li>Click the + button next to Admin Documents.</li>
                        <li>
                          Find the &quot;Request for Additional Information&quot; doc and move it to the right-hand
                          side.
                        </li>
                        <li>
                          Click Export Chart Sections → send to <CopyableContent text={referringProviderName} />
                        </li>
                      </ul>
                    </li>
                    <li>
                      c. Fill out export details
                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                        <li>
                          Type <CopyableContent text={referringProviderName} />
                        </li>
                        <li>Confirm method is set to Athena Fax and the fax number has populated.</li>
                        <li>
                          Attention box: <CopyableContent text={referringProviderName} />
                        </li>
                        <li>Note field: &quot;Request for missing information.&quot;</li>
                        <li>Confirm Submit, then Save.</li>
                      </ul>
                    </li>
                    <li>
                      d. Add Note to Patient Chart
                      <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                        <li>
                          &quot;Valerie Health: Missing Information Requested <CopyableContent text={dateReceived} />
                          &quot;
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ol>
            )}
          </div>
        </div>
      </EhrInstructionsCard>

      <AthenaReferralCards referral={referral} athenaData={athenaData} taskId={task.id} />
    </div>
  );
}
